import type { ApolloClient, FetchResult } from '@apollo/client';
import {
  cloneDeep,
  compact,
  isArray,
  isNumber,
  isObject,
  omitBy,
  sortBy,
  uniq,
} from 'lodash';
import isEqual from '@wry/equality';
import { toast } from 'react-toastify';

import type {
  ArchivedDataQuery,
  ArchivedDataQueryResult,
  ArchivedDataQueryVariables,
  CommitCargoCompartmentAllocationInput,
  CommitInput,
  CommitMutation,
  CommitMutationResult,
  CommitMutationVariables,
  CommitOrderPortCallRelationInput,
  CommitSingleEventPortCallRelationInput,
  CreateCommitLogScenarioMutationResult,
  CreateCommitLogScenarioMutationVariables,
  CreatedOrExisting,
  CurrentUserQuery,
  GetDataToCleanupForAllOrdersQueryResult,
  GetDataToCleanupForAllOrdersQueryVariables,
  HandledByCoaId,
  HandledBySpotId,
  OffHireId,
  OrderId,
  OrganizationId,
  PortCallId,
  ScenarioTimeline_OrderFragment,
  ScheduledOrderId,
  ShipmentsByExternalIdQueryResult,
  ShipmentsByExternalIdQueryVariables,
  ScenarioTimeline_ShipFragment,
  ShipId,
  ShipVoyageNamingRuleRelationId,
  SingleEventId,
  UpdateCargoCompartmentAllocationByIdInput,
  UpdateShipmentMutationResult,
  UpdateShipmentMutationVariables,
  UserId,
  VoyageCharterDataForCommitQueryResult,
  VoyageCharterDataForCommitQueryVariables,
  VoyageCharterId,
  VoyageNamingRule,
  VoyageNamingRule_AdminFragment,
  VoyageNamingRuleId,
  VoyagePrefixGetNextIdsMutationResult,
  VoyagePrefixGetNextIdsMutationVariables,
  PaymentUnit,
} from '../graphql';
import {
  ArchivedDataDocument,
  CommitDocument,
  CreateCommitLogScenarioDocument,
  GetDataToCleanupForAllOrdersDocument,
  ShipmentsByExternalIdDocument,
  UpdateShipmentDocument,
  VoyageCharterDataForCommitDocument,
  VoyagePrefixGetNextIdsDocument,
} from '../graphql';
import type {
  CommittedPlanningData,
  ScenarioOrder,
  ScenarioShipment,
} from '../lib/planningData';
import type { DeepReadOnly } from '../lib/util';
import { memoize3, memoize4 } from '../lib/util';
import {
  assertDefined,
  assertedDefined,
  delay,
  exhaustiveCheck,
  filteredMap,
  get,
  isDefined,
  mapFilter,
  mapKeyBy,
  NONE,
  setDifference,
  setSome,
} from '../lib/util';
import type { ClientId } from './core/ClientId';
import { generateClientId, parseClientId } from './core/ClientId';
import type { Entry, WithDbId } from './core/Core';
import {
  assertSameEntryType,
  cargoEntryUpdateFromTimedata,
  EventCategory,
  getPortCallId,
  isCoaEntry,
  isOffHireEntry,
  isPortCallEntry,
  isSingleEntry,
  isSpotEntry,
  isTCOrderEntry,
  singleEventTypeToEventType,
} from './core/Core';
import type { Vessel } from './core/Vessel';
import type {
  CargoEvent,
  Events,
  StaticEvent,
  TimeData,
} from './core/TimeData';
import {
  CoreEventType,
  isCargoEntry,
  isCargoEventTimeData,
  isCoaEventTimeData,
  isLoadEvent,
  isNotStaticEventTimeData,
  isOffHireEventTimeData,
  isPortCallEventTimeData,
  isSingleEventTimeData,
  isSpotEventTimeData,
  isTCOrderEventTimeData,
  PortCallEventType,
} from './core/TimeData';
import type {
  CargoEntry,
  CoaEntry,
  OffHireEntry,
  PortCallEntry,
  SingleEntry,
  SpotEntry,
  TCOrderEntry,
} from '@seaber/common/core/entries';
import type { EventTimeline, VoyageGroup } from './core/Timelines';
import { numToDateStringOpt } from './core/util';
import type {
  CommitStatus,
  EstimateTimeline,
  InternalScenarioSchedule,
  InternalUserSchedule,
  ScenarioOrderPatch,
  ScenarioOrderWithShipmentId,
} from './Scenario';
import {
  cargoAllocationEntryToInput,
  coaEntryToCoaInput,
  EntryState,
  getScenarioOrdersAndShipments,
  isEstimateScenario,
  isInternalUserScenario,
  isUserScenario,
  offHireEntryToInput,
  offHireEntryToPatch,
  portCallEntryToInput,
  portCallEntryToPatch,
  SCENARIO_DATA_SCHEMA_VERSION,
  ScenarioCommitPolicy,
  serializeScenario,
  singleEntryToInput,
  singleEntryToPatch,
  spotEntryToSpotInput,
  tcOrderEntryToScheduledOrderInput,
  tcOrderEntryToScheduledOrderPatch,
} from './Scenario';
import type {
  InternalBackupSchedule,
  InternalInitialSchedule,
  InternalMasterSchedule,
  InternalSuggestionSchedule,
  MasterSchedule,
  ScenarioSchedule,
  ScenarioShipmentExtraFreight,
  ScenarioShipmentFreight,
  SuggestionSchedule,
  UserSchedule,
} from '@seaber/common/core/scenario';
import type { CargoAllocation } from '@seaber/common/core/entries';
import {
  getAllScenarioEntries,
  getOrderAssignments,
  isEntryStateDeleted,
  isEntryStateUpdated,
} from '@seaber/common/core/scenario';
import type { CreateOrUpdateVoyageCharterArgs } from './shipment/voyageCharter';
import { createOrUpdateVoyageCharter } from './shipment/voyageCharter';
import {
  DefaultMap,
  mapGroupBy,
  mapKeyBy2,
} from '@seaber/common/lib/collections';
import { isShallowEqual } from '@seaber/common/lib/equality';
import { pluralize } from '../lib/format';
import {
  buildResetMap,
  formatVoyagePrefix,
  normalizeYearFormat,
  resolveYear,
  type VoyageNameRule,
  type VoyageNameVessel,
  type VoyageNumberingReset,
} from '@seaber/common/core/voyageName';

export type ArchivedState = {
  archivedItems: {
    orders: Set<number>;
    [EventCategory.Single]: Set<ClientId>;
    [EventCategory.OffHire]: Set<ClientId>;
    [EventCategory.PortCall]: Set<ClientId>;
  };
};

type CurrentUser = Pick<
  NonNullable<CurrentUserQuery['currentUser']>,
  'id' | 'organizationId' | 'organization'
>;

/** Compatible interface with EventTimeline but including only the fields actually required for createCommit */
export type UpdateScenarioTimelineData<EventType extends Events = Events> = {
  vessel: EventTimeline<EventType>['vessel'];
  voyageGroups: Pick<VoyageGroup<EventType>, 'events'>[];
};

const timeDataToUpdatedEntry = <T extends Exclude<Events, StaticEvent>>(
  td: TimeData<T>,
  current?: Entry,
): T['entry'] => {
  // TODO: could reuse code with addChangedEntriesToMap implementation
  if (isSingleEventTimeData(td)) {
    assertSameEntryType(td.event.entry, current);
    const { event } = td;
    const {
      duration,
      locationId,
      vesselId,
      event: eventType,
      startOverride,
      concurrent,
      seaDurationOverride,
    } = td.event;

    const change: Partial<SingleEntry> = {
      type: singleEventTypeToEventType(eventType),
      concurrent,
      startOverride: startOverride || null,
      seaDurationOverride,
      vesselId,
      locationId,
      start: td.start,
      duration,
      end: td.end,
      portCallId: getPortCallId(event),
    };

    return {
      ...(current ?? td.event.entry),
      ...change,
    };
  } else if (isOffHireEventTimeData(td)) {
    assertSameEntryType(td.event.entry, current);
    const newEntry: OffHireEntry = {
      ...td.event.entry,
      start: td.start,
      end: td.end,
      duration: td.end - td.start,
      locationAId: td.event.locationAId,
      locationBId: td.event.locationBId,
      vesselId: td.event.vesselId,
    };
    return {
      ...(current ?? td.event.entry),
      ...newEntry,
    };
  } else if (isTCOrderEventTimeData(td)) {
    assertSameEntryType(td.event.entry, current);
    return {
      ...(current ?? td.event.entry),
      ...cargoEntryUpdateFromTimedata(td),
    };
  } else if (isCoaEventTimeData(td)) {
    assertSameEntryType(td.event.entry, current);
    return {
      ...(current ?? td.event.entry),
      ...cargoEntryUpdateFromTimedata(td),
    };
  } else if (isSpotEventTimeData(td)) {
    assertSameEntryType(td.event.entry, current);
    return {
      ...(current ?? td.event.entry),
      ...cargoEntryUpdateFromTimedata(td),
    };
  } else if (isPortCallEventTimeData(td)) {
    assertSameEntryType(td.event.entry, current);

    const change: Partial<typeof td.event.entry> = {};

    switch (td.event.event) {
      case PortCallEventType.Arrival:
        change.plannedArrival = td.start;
        change.plannedArrivalOverride = td.event.startOverride;
        change.plannedArrivalDuration = td.duration || null;
        change.seaDurationOverride = td.event.seaDurationOverride;
        break;
      case PortCallEventType.Berthing:
        change.plannedBerthing = td.start;
        change.plannedBerthingOverride = td.event.startOverride;
        change.plannedBerthingDuration = td.duration || null;
        break;
      case PortCallEventType.Departure:
        change.plannedDeparture = td.start;
        change.plannedDepartureOverride = td.event.startOverride;
        change.plannedDepartureDuration = td.duration || null;
        break;
      default:
        exhaustiveCheck(td.event);
    }

    return {
      ...(current ?? td.event.entry),
      ...change,
    };
  } else {
    throw new Error(
      `Unhandled time data type when updating its entry: ${td.event.category}/${td.event.entry.clientId}`,
    );
  }
};

/** Updates all timestamps and other relevant data in scenario entries based on latest computed timelines data.
 * Relevant only for picking updates to existing entries.
 *
 * `trackChanges` controls whether entries that re-derive to a different value
 * than their previous scenario value are flagged as `EntryState.UPDATED`.
 * Pass `false` during commit-time normalization where the re-derivation is
 * not a user action — interactive edits already flag their own entries via
 * the scenario reducer, so this function does not need to add new flags
 * there. SEABER-6624: setting flags here turned timeline-noise on untouched
 * port calls into phantom `updatePortCalls` patches.
 */
export function updateScenarioFromTimelines<
  T extends InternalUserSchedule | InternalSuggestionSchedule,
>(
  scenario: T,
  timelines: UpdateScenarioTimelineData[],
  { trackChanges = true }: { trackChanges?: boolean } = {},
): T {
  const updatedEntries = new DefaultMap<EventCategory, Map<ClientId, Entry>>(
    () => new Map(),
  );
  for (const timeline of timelines) {
    for (const voyageGroup of timeline.voyageGroups) {
      for (const td of voyageGroup.events) {
        /* Static events cannot be updated */
        if (!isNotStaticEventTimeData(td)) {
          continue;
        }

        const updated = timeDataToUpdatedEntry(
          td,
          updatedEntries.get(td.event.category).get(td.event.entry.clientId),
        );
        updatedEntries
          .get(td.event.category)
          .set(td.event.entry.clientId, updated);
      }
    }
  }

  const updatedScenario: Pick<
    InternalUserSchedule,
    | 'schedule'
    | 'coaAssignments'
    | 'spotAssignments'
    | 'portCalls'
    | 'entryStates'
  > = {
    schedule: [],
    coaAssignments: [],
    spotAssignments: [],
    portCalls: [],
    entryStates: new Map(scenario.entryStates),
  };

  const oldEntries = mapKeyBy2(
    [
      ...scenario.schedule,
      ...scenario.coaAssignments,
      ...scenario.spotAssignments,
      ...scenario.portCalls,
    ],
    (e) => e.category,
    (e) => e.clientId,
  );

  for (const entryMap of updatedEntries.values()) {
    for (const entry of entryMap.values()) {
      if (
        isTCOrderEntry(entry) ||
        isSingleEntry(entry) ||
        isOffHireEntry(entry)
      ) {
        updatedScenario.schedule.push(entry);
      } else if (isCoaEntry(entry)) {
        updatedScenario.coaAssignments.push(entry);
      } else if (isSpotEntry(entry)) {
        updatedScenario.spotAssignments.push(entry);
      } else if (isPortCallEntry(entry)) {
        updatedScenario.portCalls.push(entry);
      } else {
        exhaustiveCheck(entry);
      }

      if (trackChanges) {
        const oldEntry = oldEntries.get(entry.category).get(entry.clientId);
        if (oldEntry) {
          if (!isEqual(oldEntry, entry)) {
            updatedScenario.entryStates.set(entry.clientId, EntryState.UPDATED);
          }
        }
      }
    }
  }

  return {
    ...scenario,
    ...updatedScenario,
  };
}

let warnedLoadPortCallArchived = false;
let warnedDischargePortCallArchived = false;
let warnedArchivedSingleEventPortCall = false;

/** Updates the scenario by removing any traces of archived items.
 * Recommended to recompute the timelines after this and then update the scenario again with updateScenarioFromTimelines.
 */
export function updateScenarioFromArchivedState<
  T extends
    | InternalUserSchedule
    | InternalSuggestionSchedule
    | InternalMasterSchedule
    | InternalBackupSchedule
    | InternalInitialSchedule,
>(
  scenario: T,
  archivedState: ArchivedState,
  opts: {
    /** At commit time we shouldn't do unlinking, it's fine whatever port call an assignment refers to.
     * This can happen when using merge commit and an assignment in master (not present in scenario) has a port call archived already.
     * (to be fixed; should not allow archiving port calls while all assignments aren't archived) */
    unlinkArchivedPortCalls: boolean;
  } = {
    unlinkArchivedPortCalls: true,
  },
): T {
  const {
    archivedItems: {
      orders,
      OFF: offHires,
      PC: portCalls,
      SE: singleEvents,
      ...rest
    },
  } = archivedState;
  if (Object.keys(rest).length > 0) {
    throw new Error(`Unhandled items in archived state`);
  }

  const orderEntryIds = new Set<ClientId>();
  for (const e of scenario.schedule) {
    if (isTCOrderEntry(e) && orders.has(e.orderId)) {
      orderEntryIds.add(e.clientId);
    }
  }
  for (const e of scenario.spotAssignments) {
    if (orders.has(e.orderId)) {
      orderEntryIds.add(e.clientId);
    }
  }
  for (const e of scenario.coaAssignments) {
    if (orders.has(e.orderId)) {
      orderEntryIds.add(e.clientId);
    }
  }

  const allArchivedCIds = new Set<ClientId>([
    ...orderEntryIds,
    ...offHires,
    ...portCalls,
    ...singleEvents,
  ]);

  /** Checks the entry whether it refers to an archived port call id and sets them to null.
   * If archiving works correctly this situation shouldn't ever happen, but better to be sure.
   */
  const unlinkArchivedPortCallId = <T extends Entry>(e: T): T => {
    if (!opts.unlinkArchivedPortCalls) return e;
    if (isSingleEntry(e) && e.portCallId) {
      if (portCalls.has(e.portCallId)) {
        if (!warnedArchivedSingleEventPortCall) {
          warnedArchivedSingleEventPortCall = true;
          console.warn(`Single event refers to a port call that was archived`);
        }
        return { ...e, portCallId: null };
      }
    } else if (isTCOrderEntry(e) || isCoaEntry(e) || isSpotEntry(e)) {
      let result: TCOrderEntry | CoaEntry | SpotEntry = e;
      if (result.loadingPortCallId && portCalls.has(result.loadingPortCallId)) {
        if (!warnedLoadPortCallArchived) {
          warnedLoadPortCallArchived = true;
          console.warn(`Load event refers to a port call that was archived`);
        }
        result = { ...result, loadingPortCallId: null };
      }
      if (
        result.dischargingPortCallId &&
        portCalls.has(result.dischargingPortCallId)
      ) {
        if (!warnedDischargePortCallArchived) {
          warnedDischargePortCallArchived = true;
          console.warn(
            `Discharge event ${e.category}/${e.clientId} refers to a port call that was archived ${e.dischargingPortCallId}`,
          );
        }
        result = { ...result, dischargingPortCallId: null };
      }
      return result as T;
    }
    return e;
  };

  return {
    ...scenario,

    // Drop all archived item entries from their respective arrays
    schedule: scenario.schedule
      .filter((e) => !allArchivedCIds.has(e.clientId))
      .map(unlinkArchivedPortCallId),
    spotAssignments: scenario.spotAssignments
      .filter((e) => !orders.has(e.orderId))
      .map(unlinkArchivedPortCallId),
    coaAssignments: scenario.coaAssignments
      .filter((e) => !orders.has(e.orderId))
      .map(unlinkArchivedPortCallId),
    portCalls: scenario.portCalls.filter((e) => !portCalls.has(e.clientId)),

    voyageCharterByOrderId: new Map(
      [...scenario.voyageCharterByOrderId].filter(
        ([orderId]) => !orders.has(orderId),
      ),
    ),
    cargoAllocations: scenario.cargoAllocations?.filter(
      (allocation) => !orders.has(allocation.orderId),
    ),

    ...(isInternalUserScenario(scenario)
      ? {
          // Drop all archived items from entryStates
          entryStates: mapFilter(
            scenario.entryStates,
            (_v, k) => !allArchivedCIds.has(k),
          ),
        }
      : null),
  };
}

function created(
  commitArr: unknown[],
): CreatedOrExisting & { created: number } {
  return { created: commitArr.length - 1 };
}

/** Adds an item to commit input for create, returns CreatedOrExisting */
function createEntry(
  commit: CommitInput,
  e: Entry,
  opts: { currentOrgId: OrganizationId; currentUserId: UserId },
): CreatedOrExisting {
  if (isTCOrderEntry(e)) {
    (commit.createScheduledOrders ??= []).push(
      tcOrderEntryToScheduledOrderInput({
        ...e,
        planningOrganisationId: opts.currentOrgId,
        userId: opts.currentUserId,
        locked: true,
      }),
    );
    return created(commit.createScheduledOrders);
  } else if (isSingleEntry(e)) {
    (commit.createSingleEvents ??= []).push(
      singleEntryToInput({
        ...e,
        planningOrganisationId: opts.currentOrgId,
        userId: opts.currentUserId,
      }),
    );
    return created(commit.createSingleEvents);
  } else if (isOffHireEntry(e)) {
    (commit.createOffHires ??= []).push(offHireEntryToInput(e));
    return created(commit.createOffHires);
  } else if (isSpotEntry(e)) {
    (commit.createHandledBySpots ??= []).push(spotEntryToSpotInput(e));
    return created(commit.createHandledBySpots);
  } else if (isCoaEntry(e)) {
    (commit.createHandledByCoas ??= []).push(coaEntryToCoaInput(e));
    return created(commit.createHandledByCoas);
  } else if (isPortCallEntry(e)) {
    (commit.createPortCalls ??= []).push(portCallEntryToInput(e));
    return created(commit.createPortCalls);
  } else {
    exhaustiveCheck(e);
  }
}

export type EntryDelta<T> = Partial<T>;

/** Returns updated fields from *original* to *updated*. The fields that are numbers are returned as a delta, other types as the updated value.
 * The comparison only considers keys present in *updated*. This means that if *updated* is a patch object compared against *original*
 * and it doesn't include a value then it means we don't care about updating it regardless of what the original value is.
 */
export function entryDelta<T, U extends object>(
  original: T,
  updated: U,
  ...omitKeys: string[]
): EntryDelta<U> {
  const result: EntryDelta<U> = {};
  for (const k of Object.keys(updated)) {
    if (omitKeys.includes(k)) {
      continue;
    }
    const av: unknown = original[k as keyof T];
    const bv: unknown = updated[k as keyof U];
    if (av !== bv) {
      if (Array.isArray(av) && isEqual(av, bv)) {
        continue;
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        result[k as keyof U] = (bv - av) as U[keyof U];
      } else {
        result[k as keyof U] = bv as U[keyof U];
      }
    }
  }
  return result;
}

// Metadata of dbId, updatedAt, archived are not significant as changes, so we can omit those when checking for equality.
// Port call ids are relevant, but it's handled in relations, not the entry table itself.
export const ENTRY_METADATA_OMIT_KEYS = [
  'dbId',
  'updatedAt',
  'archived',
  'portCallId',
  'loadingPortCallId',
  'dischargingPortCallId',
] as const;

// Derived port-call timings, recomputed from the schedule rather than authored by the user; omitted from the unsaved-changes diff so a moved voyage (or master recompute on commit) doesn't report phantom port-call changes.
const PORT_CALL_DERIVED_TIMING_KEYS = [
  'plannedArrival',
  'plannedBerthing',
  'plannedDeparture',
] as const;

// Combined omit list for comparing port-call entries in the unsaved-changes diff (metadata + derived timings), precomputed so the per-entry comparator doesn't rebuild it on every call.
const PORT_CALL_COMPARISON_OMIT_KEYS = [
  ...ENTRY_METADATA_OMIT_KEYS,
  ...PORT_CALL_DERIVED_TIMING_KEYS,
] as const;

// Bunker overrides/price sources are auto-populated by cost derivation and reset to null on a fresh recompute (e.g. the commit-time rebuild), so a recomputed entry shows null against a still-populated baseline. We ignore these only when the live entry has no selection — never when the user actually set one, which must still count.
const PORT_CALL_AUTO_POPULATED_BUNKER_KEYS = [
  'bunkerOverrides',
  'bunkerPriceSource',
] as const;

const hasNoBunkerSelection = (value: unknown): boolean =>
  value == null || (Array.isArray(value) && value.length === 0);

/** We use a custom check instead of just isEqual so that we can drop comparisons between values where a key is totally missing.
 * This can happen at least if we have added new optional fields to scenario entries and scenario doesn't have the value but db does.
 * In this case we wouldn't be updating the saved value with null.
 * Original requirement for this: totalLoadDurationOverride was added to PortCallEntry as an optional field so that we didn't have to
 * force clearing of user scenarios for this update to be compatible. When committed state is fetched this field was already present there though.
 */
export function doesEntryNeedUpdate<T, U extends object>(
  original: T,
  updated: U,
  ...omitKeys: string[]
): boolean {
  return (
    Object.keys(entryDelta<T, U>(original, updated, ...omitKeys)).length > 0
  );
}

/** Diffable scenario content for the unsaved-changes badge: the four entry arrays plus cargo allocations and freight maps. These last three aren't `Entry`s, so they can't ride along in `getAllScenarioEntries`. */
export type ScenarioContentSnapshot = {
  entries: Entry[];
  cargoAllocations: CargoAllocation[];
  freightByShipmentClientId: Map<ClientId, ScenarioShipmentFreight>;
  extraFreightByShipmentClientId: Map<ClientId, ScenarioShipmentExtraFreight[]>;
};

/** Extracts the diffable content from a scenario. Returns references; the caller (captureScenarioSnapshot) clones the whole snapshot. */
export function getScenarioContentSnapshot(
  scenario: ScenarioSchedule,
): ScenarioContentSnapshot {
  return {
    entries: getAllScenarioEntries(scenario),
    cargoAllocations: scenario.cargoAllocations,
    freightByShipmentClientId: scenario.freightByShipmentClientId,
    extraFreightByShipmentClientId: scenario.extraFreightByShipmentClientId,
  };
}

/** Per-action change tally for one diff category. */
type ChangeCounts = { added: number; removed: number; changed: number };

const totalChanges = (c: ChangeCounts): number =>
  c.added + c.removed + c.changed;

const addChangeCounts = (a: ChangeCounts, b: ChangeCounts): ChangeCounts => ({
  added: a.added + b.added,
  removed: a.removed + b.removed,
  changed: a.changed + b.changed,
});

/** Per-action lists of the affected items for one diff category. */
type ChangeLists<T> = { added: T[]; removed: T[]; changed: T[] };

const countsOf = <T>(lists: ChangeLists<T>): ChangeCounts => ({
  added: lists.added.length,
  removed: lists.removed.length,
  changed: lists.changed.length,
});

/** Diffs two clientId-keyed collections into added (only current), removed (only snapshot), changed (in both, unequal via `isDifferent`). */
function diffByClientIdDetailed<T>(
  current: Map<ClientId, T>,
  snapshot: Map<ClientId, T>,
  isDifferent: (prev: T, curr: T) => boolean,
): ChangeLists<T> {
  const lists: ChangeLists<T> = { added: [], removed: [], changed: [] };
  for (const [clientId, value] of current) {
    const prev = snapshot.get(clientId);
    if (!prev) {
      lists.added.push(value);
    } else if (isDifferent(prev, value)) {
      lists.changed.push(value);
    }
  }
  for (const [clientId, value] of snapshot) {
    if (!current.has(clientId)) {
      lists.removed.push(value);
    }
  }
  return lists;
}

const diffByClientId = <T>(
  current: Map<ClientId, T>,
  snapshot: Map<ClientId, T>,
  isDifferent: (prev: T, curr: T) => boolean,
): ChangeCounts =>
  countsOf(diffByClientIdDetailed(current, snapshot, isDifferent));

/** Categorized scenario diff: entries split into voyages (cargo/single/off-hire) and port calls, plus allocations and freight. Voyages keep their entry lists so the breakdown can name them. */
type UnsavedChangesDiff = {
  voyages: ChangeLists<Entry>;
  portCalls: ChangeCounts;
  cargoAllocations: ChangeCounts;
  freight: ChangeCounts;
};

/** Sum of added+removed+changed across every diff category. */
const totalDiff = (diff: UnsavedChangesDiff): number =>
  totalChanges(countsOf(diff.voyages)) +
  totalChanges(diff.portCalls) +
  totalChanges(diff.cargoAllocations) +
  totalChanges(diff.freight);

/** Categorizes each scenario-vs-snapshot change by domain and action; backs both the total count and the human breakdown. */
function diffUnsavedChanges(
  scenario: ScenarioSchedule,
  snapshot: ScenarioContentSnapshot,
): UnsavedChangesDiff {
  const current = getScenarioContentSnapshot(scenario);

  const splitEntries = (entries: Entry[]) => ({
    voyages: new Map(
      entries.filter((e) => !isPortCallEntry(e)).map((e) => [e.clientId, e]),
    ),
    portCalls: new Map(
      entries.filter(isPortCallEntry).map((e) => [e.clientId, e]),
    ),
  });
  const currentEntries = splitEntries(current.entries);
  const snapshotEntries = splitEntries(snapshot.entries);

  const entryDiffers = (prev: Entry, curr: Entry): boolean =>
    doesEntryNeedUpdate(prev, curr, ...ENTRY_METADATA_OMIT_KEYS);

  // Port calls always ignore their derived planned timings; bunker fields are ignored only when the live entry has no selection, so a genuine user-set bunker override still counts.
  const portCallDiffers = (prev: Entry, curr: Entry): boolean => {
    const omitKeys: string[] = [...PORT_CALL_COMPARISON_OMIT_KEYS];
    if (isPortCallEntry(curr)) {
      for (const key of PORT_CALL_AUTO_POPULATED_BUNKER_KEYS) {
        if (hasNoBunkerSelection(curr[key])) omitKeys.push(key);
      }
    }
    return doesEntryNeedUpdate(prev, curr, ...omitKeys);
  };

  // Freight and extra-freight share the "freight change(s)" category, so their diffs are summed.
  const freight = addChangeCounts(
    diffByClientId(
      current.freightByShipmentClientId,
      snapshot.freightByShipmentClientId,
      (prev, curr) => !isEqual(prev, curr),
    ),
    diffByClientId(
      current.extraFreightByShipmentClientId,
      snapshot.extraFreightByShipmentClientId,
      (prev, curr) => !isEqual(prev, curr),
    ),
  );

  return {
    voyages: diffByClientIdDetailed(
      currentEntries.voyages,
      snapshotEntries.voyages,
      entryDiffers,
    ),
    portCalls: diffByClientId(
      currentEntries.portCalls,
      snapshotEntries.portCalls,
      portCallDiffers,
    ),
    cargoAllocations: diffByClientId(
      new Map(current.cargoAllocations.map((a) => [a.clientId, a])),
      new Map(snapshot.cargoAllocations.map((a) => [a.clientId, a])),
      (prev, curr) => !isEqual(prev, curr),
    ),
    freight,
  };
}

/** One breakdown row; `indent` marks a voyage named under its category header. */
export type SummaryLine = { text: string; indent?: boolean };

export type ScenarioDirtyState = {
  dirty: boolean;
  count: number;
  summary: SummaryLine[];
};

/** Resolves a voyage's order and vessel to display names; lets the breakdown name which voyage changed without coupling this module to the GraphQL fragment shapes. */
export type UnsavedChangesResolvers = {
  orderName: (orderId: OrderId) => string | undefined;
  vesselName: (vesselId: ShipId) => string | undefined;
};

/** Dirty iff the scenario's content differs from the baseline it would reload to; pure content diff so it survives refresh and never counts master-synced entries. */
export function scenarioDirtyState(
  scenario: ScenarioSchedule,
  snapshot: ScenarioContentSnapshot | undefined,
  resolvers?: UnsavedChangesResolvers,
): ScenarioDirtyState {
  // Without a baseline (pre-load) there is nothing to diff against, so treat as clean.
  if (!snapshot) return { dirty: false, count: 0, summary: [] };
  const { lines, count } = summarizeUnsavedChanges(
    scenario,
    snapshot,
    resolvers,
  );
  return { dirty: count > 0, count, summary: lines };
}

/** The legacy "changed" tab-label asterisk shows only for non-shared scenarios with local edits; shared scenarios use the unsaved-changes badge instead. */
export const showsChangedAsterisk = (scenario: ScenarioSchedule): boolean =>
  !scenario.shared && Boolean(scenario.changedAt);

/** Counts scenario content differing from the last-saved snapshot; used by the shared-scenario unsaved-changes badge. */
export function countUnsavedChanges(
  scenario: ScenarioSchedule,
  snapshot: ScenarioContentSnapshot,
): number {
  return totalDiff(diffUnsavedChanges(scenario, snapshot));
}

/** `countNoun` pluralizes via the tagged-template helper, so the noun must be a literal at the call site. */
function summarizeCategory(
  counts: ChangeCounts,
  countNoun: (n: number) => string,
): SummaryLine[] {
  const lines: SummaryLine[] = [];
  if (counts.added > 0)
    lines.push({ text: `${countNoun(counts.added)} added` });
  if (counts.removed > 0)
    lines.push({ text: `${countNoun(counts.removed)} removed` });
  if (counts.changed > 0)
    lines.push({ text: `${countNoun(counts.changed)} changed` });
  return lines;
}

/** Only cargo voyages (TC/COA/spot) carry an order to name. */
function voyageOrderName(
  entry: Entry,
  resolvers: UnsavedChangesResolvers,
): string | undefined {
  if (isTCOrderEntry(entry) || isCoaEntry(entry) || isSpotEntry(entry)) {
    return resolvers.orderName(entry.orderId);
  }
  return undefined;
}

/** The assigned vessel; present on every voyage entry type, null while unassigned. */
function voyageVesselName(
  entry: Entry,
  resolvers: UnsavedChangesResolvers,
): string | undefined {
  const vesselId =
    isTCOrderEntry(entry) ||
    isCoaEntry(entry) ||
    isSpotEntry(entry) ||
    isSingleEntry(entry) ||
    isOffHireEntry(entry)
      ? entry.vesselId
      : null;
  return vesselId != null ? resolvers.vesselName(vesselId) : undefined;
}

/** "<cargo> on <vessel>" when both resolve, else whichever does; the vessel is the timeline's primary locator. */
function voyageLabel(
  entry: Entry,
  resolvers: UnsavedChangesResolvers | undefined,
): string | undefined {
  if (!resolvers) return undefined;
  const order = voyageOrderName(entry, resolvers);
  const vessel = voyageVesselName(entry, resolvers);
  if (order && vessel) return `${order} on ${vessel}`;
  return order ?? vessel;
}

/** Cap on named voyage lines per action; the rest collapse into an indented "+N more" so the menu stays bounded. */
const MAX_NAMED_VOYAGES = 6;

/** Builds the voyage breakdown: a count header per action ("3 voyages added"), then each named voyage on its own indented line so a user can tell their own change apart from a collaborator's synced one. */
function summarizeVoyages(
  voyages: ChangeLists<Entry>,
  resolvers: UnsavedChangesResolvers | undefined,
): SummaryLine[] {
  const lines: SummaryLine[] = [];
  const group = (entries: Entry[], action: string): void => {
    if (entries.length === 0) return;
    lines.push({ text: `${pluralize`${entries.length} voyage`} ${action}` });
    const labels = entries
      .map((e) => voyageLabel(e, resolvers))
      .filter((label): label is string => label !== undefined);
    const shown = labels.slice(0, MAX_NAMED_VOYAGES);
    for (const label of shown) lines.push({ text: label, indent: true });
    // "+N more" covers every voyage past the shown ones (capped or unnamed) so the lines reconcile with the header
    // count; suppressed when nothing is named, since a lone "+N more" under a header reads worse than just the count.
    const extra = entries.length - shown.length;
    if (shown.length > 0 && extra > 0) {
      lines.push({ text: `+${extra} more`, indent: true });
    }
  };
  group(voyages.added, 'added');
  group(voyages.removed, 'removed');
  group(voyages.changed, 'changed');
  return lines;
}

/** Categorized human breakdown of the unsaved changes. The leading counts sum to `countUnsavedChanges` for the same inputs; voyage lines also name the changed order(s) when `resolvers` is supplied. */
export function summarizeUnsavedChanges(
  scenario: ScenarioSchedule,
  snapshot: ScenarioContentSnapshot,
  resolvers?: UnsavedChangesResolvers,
): { lines: SummaryLine[]; count: number } {
  const diff = diffUnsavedChanges(scenario, snapshot);
  const lines: SummaryLine[] = [
    ...summarizeVoyages(diff.voyages, resolvers),
    ...summarizeCategory(diff.portCalls, (n) => pluralize`${n} port call`),
    ...summarizeCategory(
      diff.cargoAllocations,
      (n) => pluralize`${n} cargo allocation`,
    ),
    ...summarizeCategory(diff.freight, (n) => pluralize`${n} freight change`),
  ];
  return { lines, count: totalDiff(diff) };
}

/** Adds an item to commit input for update, returns CreatedOrExisting */
function updateEntry(
  commit: CommitInput,
  e: Entry,
  existing: WithDbId<Entry, number>,
  deltas: [Entry, Partial<Entry>][],
): CreatedOrExisting {
  const { dbId } = existing;

  const delta = entryDelta(existing, e, ...ENTRY_METADATA_OMIT_KEYS);
  if (Object.keys(delta).length === 0) {
    return { existing: dbId };
  }

  if (isTCOrderEntry(e)) {
    (commit.updateScheduledOrders ??= []).push({
      id: dbId as ScheduledOrderId,
      scheduledOrderPatch: tcOrderEntryToScheduledOrderPatch(e),
    });
  } else if (isSingleEntry(e)) {
    (commit.updateSingleEvents ??= []).push({
      id: dbId as SingleEventId,
      singleEventPatch: singleEntryToPatch(e),
    });
  } else if (isOffHireEntry(e)) {
    (commit.updateOffHires ??= []).push({
      id: dbId as OffHireId,
      offHirePatch: offHireEntryToPatch(e),
    });
  } else if (isSpotEntry(e)) {
    (commit.updateHandledBySpots ??= []).push({
      id: dbId as HandledBySpotId,
      handledBySpotPatch: spotEntryToSpotInput(e),
    });
  } else if (isCoaEntry(e)) {
    (commit.updateHandledByCoas ??= []).push({
      id: dbId as HandledByCoaId,
      handledByCoaPatch: coaEntryToCoaInput(e),
    });
  } else if (isPortCallEntry(e)) {
    (commit.updatePortCalls ??= []).push({
      id: dbId as PortCallId,
      portCallPatch: portCallEntryToPatch(e),
    });
  } else {
    exhaustiveCheck(e);
  }
  deltas.push([e, delta]);
  return { existing: dbId };
}

const pushIfNotExist = <T>(arr: T[], v: T): T[] => {
  if (!arr.includes(v)) {
    arr.push(v);
  }
  return arr;
};

/** Adds an item to commit input for delete */
function deleteEntry(
  commit: CommitInput,
  e: WithDbId<Entry, number>,
  assignmentCleanupData: AssignmentCleanupDataForCommit,
  scenario: UserSchedule | SuggestionSchedule,
  deletedCargoEntryOrderIds: Set<number>,
) {
  let operationArray: number[];

  if (isTCOrderEntry(e)) {
    operationArray = commit.deleteScheduledOrders ??= [];
  } else if (isSingleEntry(e)) {
    operationArray = commit.deleteSingleEvents ??= [];
  } else if (isOffHireEntry(e)) {
    operationArray = commit.deleteOffHires ??= [];
  } else if (isSpotEntry(e)) {
    operationArray = commit.deleteHandledBySpots ??= [];
  } else if (isCoaEntry(e)) {
    operationArray = commit.deleteHandledByCoas ??= [];
  } else if (isPortCallEntry(e)) {
    operationArray = commit.deletePortCalls ??= [];
  } else {
    exhaustiveCheck(e);
  }

  pushIfNotExist(operationArray, e.dbId);

  if (isCargoEntry(e)) {
    // Clean up deleted assignments. Some of these may or may not be handled with other branches too.
    const orderId = e.orderId;
    deletedCargoEntryOrderIds.add(orderId);
    const cleanupData = assignmentCleanupData.get(orderId);
    if (cleanupData) {
      for (const bol of compact(cleanupData.billOfLadingDataByOrderId.nodes)) {
        pushIfNotExist((commit.deleteBillOfLadingData ??= []), bol.id);
      }
      for (const cm of compact(cleanupData.cargoMeasurementsByOrderId.nodes)) {
        pushIfNotExist((commit.deleteCargoMeasurements ??= []), cm.id);
      }
      for (const rel of compact(
        cleanupData.orderPortCallRelationsByOrderId.nodes,
      )) {
        pushIfNotExist((commit.deleteOrderPortCallRelations ??= []), rel.id);
      }
      for (const rel of compact(
        cleanupData.cargoCompartmentAllocationsByOrderId.nodes,
      )) {
        pushIfNotExist(
          (commit.deleteCargoCompartmentAllocations ??= []),
          rel.id,
        );
      }
      for (const rel of compact(
        cleanupData.orderVoyageCharterRelationsByOrderId.nodes,
      )) {
        if (
          scenario.voyageCharterByOrderId.get(orderId) !== rel.voyageCharterId
        ) {
          pushIfNotExist(
            (commit.deleteOrderVoyageCharterRelations ??= []),
            rel.id,
          );
        }
      }
    }
  }
}

function createOrderPortCallRelation(
  commit: CommitInput,
  rel: CommitOrderPortCallRelationInput,
): void {
  (commit.createOrderPortCallRelations ??= []).push(rel);
}
function deleteOrderPortCallRelation(
  commit: CommitInput,
  rel: { id: number },
): void {
  pushIfNotExist((commit.deleteOrderPortCallRelations ??= []), rel.id);
}
function createSingleEventPortCallRelation(
  commit: CommitInput,
  rel: CommitSingleEventPortCallRelationInput,
): void {
  (commit.createSingleEventPortCallRelations ??= []).push(rel);
}
function deleteSingleEventPortCallRelation(
  commit: CommitInput,
  rel: { id: number },
): void {
  pushIfNotExist((commit.deleteSingleEventPortCallRelations ??= []), rel.id);
}

export type CreateCommitOptions = {
  policy: ScenarioCommitPolicy;
  currentUser: CurrentUser;
  /** Do not use if unsure. Can set to true if the result is to be used for a preview. Uses more relaxed rules for some checks, e.g. not requiring shipment names. */
  forDryRun?: boolean;
  debug?: boolean;
  suppressEmail: boolean;
  /**
   * When set, restrict the commit to changes belonging to these vessels (a
   * vessel-scoped save). Already the *expanded* scope from `computeScopeClosure`
   * — every entangled vessel is included — so the engine never has to widen it
   * further. `undefined` means an all-vessels save (today's behavior).
   */
  scopeVesselIds?: ReadonlySet<number>;
};

export function isEmptyCommit(input: CommitInput): boolean {
  for (const val of Object.values(input)) {
    if (isArray(val) && val.length > 0) {
      return false;
    }
  }
  return true;
}

/** Compares persisted state to scenario and returns a kind of diff that can be committed in one transaction. */
export function createCommitInput(
  committedData: CommittedPlanningData,
  scenario: UserSchedule | SuggestionSchedule,
  voyageCharterData: ParsedVoyageCharterDataForCommit | null,
  shipmentData: ShipmentDataForCommit,
  scenarioOrders: Map<number, ScenarioOrder>,
  assignmentCleanupData: AssignmentCleanupDataForCommit,
  opts: CreateCommitOptions,
  deletedCargoEntryOrderIds: Set<number> = new Set(),
  usedScenarioShipments: ScenarioShipment[],
): {
  commit: CommitInput;
  deltas: [Entry, Partial<Entry>][];
  /**
   * Client ids of the in-scope entries this commit actually wrote. `null` for an
   * all-vessels commit (every change was committed). For a scoped commit the
   * caller clears only these entries' change-tracking and leaves out-of-scope
   * changes pending (see vessel-scoped-commits spec — out-of-scope changes stay
   * on the scenario, just not committed).
   */
  committedEntryClientIds: Set<ClientId> | null;
} {
  const { suppressEmail, policy, currentUser } = opts;
  // Note: archived items are not supposed to be in the result, but we make sure of it anyway to avoid a disaster in case of a future bug in query (see SEABER-1278)
  const allCommittedEntries = [
    ...committedData.coaEntries.values(),
    ...committedData.spotEntries.values(),
    ...committedData.allScheduleData.values(),
    ...committedData.portCallEntriesByClientId.values(),
  ].filter((e) => !e.archived);

  const committedEntriesByCId = mapKeyBy(
    allCommittedEntries,
    (e) => e.clientId,
  );

  const allScenarioEntries: Entry[] = getAllScenarioEntries(scenario).filter(
    (e) => {
      const dbId = e.dbId;
      if (!dbId) return true;
      switch (e.category) {
        case EventCategory.PortCall:
          return true;
        case EventCategory.TC:
        case EventCategory.COA:
        case EventCategory.Spot:
          return !committedData.completedOrderIds.has(e.orderId);
        case EventCategory.Single:
          return !committedData.completedSingleEventIds.has(dbId);
        case EventCategory.OffHire:
          return !committedData.completedOffHireIds.has(dbId);
        default:
          return exhaustiveCheck(e);
      }
    },
  );
  const scenarioEntriesByCId = mapKeyBy(allScenarioEntries, (e) => e.clientId);

  const allSingleEventEntriesByClientId = mapKeyBy(
    committedData.allScheduleData.filter(
      (e): e is WithDbId<Required<SingleEntry>, SingleEventId> =>
        isSingleEntry(e),
    ),
    (e) => e.clientId,
  );

  const committedOrderAssignmentsByOrderId = new Map(
    [
      ...committedData.tcOrderEntries.values(),
      ...committedData.completedTcOrderEntries.values(),
      ...committedData.spotEntries.values(),
      ...committedData.coaEntries.values(),
    ].map((e) => [e.orderId, e]),
  );

  // --- Vessel-scoped commit (see vessel-scoped-commits spec) ---
  // `scopeVesselIds`, when set, is the already-expanded scope. Cargo entries are
  // scoped by their order's assignment vessel and port calls by reference
  // (below), not by an entry's own `vesselId` — COA/spot port-call entries can
  // carry a null `vesselId`.
  const { scopeVesselIds } = opts;
  const scenarioVesselByOrderId: Map<number, number | null> | null =
    scopeVesselIds
      ? new Map(
          [...getOrderAssignments(scenario)].map(
            ([orderId, e]): [number, number | null] => [orderId, e.vesselId],
          ),
        )
      : null;
  const masterVesselByOrderId: Map<number, number | null> | null =
    scopeVesselIds
      ? new Map<number, number | null>(
          [...committedOrderAssignmentsByOrderId.values()].map(
            (e): [number, number | null] => [e.orderId, e.vesselId],
          ),
        )
      : null;
  const vesselInScope = (vesselId: number | null | undefined): boolean =>
    !scopeVesselIds || (vesselId != null && scopeVesselIds.has(vesselId));
  const orderVessels = (
    orderId: number,
  ): { scenarioVessel: number | null; masterVessel: number | null } => ({
    scenarioVessel: scenarioVesselByOrderId?.get(orderId) ?? null,
    masterVessel: masterVesselByOrderId?.get(orderId) ?? null,
  });
  const orderInScope = (orderId: number): boolean => {
    if (!scopeVesselIds) return true;
    const { scenarioVessel, masterVessel } = orderVessels(orderId);
    // An order with no vessel on either side has no vessel to scope by, so it
    // is always saved (vessel-scoped-commits spec). This covers spot/relet
    // voyages whose assignment and port calls carry no vesselId.
    if (scenarioVessel === null && masterVessel === null) return true;
    return vesselInScope(scenarioVessel) || vesselInScope(masterVessel);
  };
  /** Orders with no vessel on either side are always saved, regardless of scope. */
  const orderHasNoVessel = (orderId: number): boolean => {
    const { scenarioVessel, masterVessel } = orderVessels(orderId);
    return scenarioVessel === null && masterVessel === null;
  };
  // A port call is in scope when an in-scope cargo entry (load/discharge) or an
  // in-scope single event refers to it. Reference-driven inclusion keeps a
  // shared port call attached to whichever in-scope voyage needs it, so an
  // in-scope order never loses its port-call relation.
  const inScopePortCallIds = new Set<ClientId>();
  if (scopeVesselIds) {
    for (const entry of allScenarioEntries) {
      if (isCargoEntry(entry)) {
        if (!orderInScope(entry.orderId)) continue;
        if (entry.loadingPortCallId) {
          inScopePortCallIds.add(entry.loadingPortCallId);
        }
        if (entry.dischargingPortCallId) {
          inScopePortCallIds.add(entry.dischargingPortCallId);
        }
      } else if (isSingleEntry(entry)) {
        if (vesselInScope(entry.vesselId) && entry.portCallId) {
          inScopePortCallIds.add(entry.portCallId);
        }
      }
    }
  }
  const entryInScope = (entry: Entry): boolean => {
    if (!scopeVesselIds) return true;
    if (isCargoEntry(entry)) return orderInScope(entry.orderId);
    if (isSingleEntry(entry) || isOffHireEntry(entry)) {
      return vesselInScope(entry.vesselId);
    }
    if (isPortCallEntry(entry)) {
      return inScopePortCallIds.has(entry.clientId);
    }
    return exhaustiveCheck(entry);
  };
  // A scenario shipment (voyage) is in scope when any of its orders is. The
  // shipment create/update and freight operations key off the shipment, not an
  // entry, so without this a new voyage on an out-of-scope vessel still emits a
  // createShipments op — surfacing as an out-of-scope "New voyages" count on a
  // save the user scoped away.
  const scopeOrderIdsByShipmentCId = new Map<ClientId, number[]>();
  if (scopeVesselIds) {
    for (const [orderId, order] of scenarioOrders) {
      if (!order.shipmentId) continue;
      const list = scopeOrderIdsByShipmentCId.get(order.shipmentId);
      if (list) {
        list.push(orderId);
      } else {
        scopeOrderIdsByShipmentCId.set(order.shipmentId, [orderId]);
      }
    }
  }
  const shipmentInScope = (shipmentClientId: ClientId): boolean =>
    !scopeVesselIds ||
    (scopeOrderIdsByShipmentCId.get(shipmentClientId)?.some(orderInScope) ??
      false);

  const result: CommitInput = {
    scenarioCID: scenario.clientId,
    isMerge: policy === ScenarioCommitPolicy.MERGE_COMPATIBLE,
    previousCommitAt: numToDateStringOpt(scenario.masterCommitAt),
    suppressEmail,
  };

  // For all entries created or existing we will add it here so that it can be referred to in relations
  const createdOrExistingByCId = new Map<ClientId, CreatedOrExisting>();

  const { cargoAllocations, committedOrderById, shipmentByClientId } =
    committedData;

  const deltas: [Entry, Partial<Entry>][] = [];

  // Handle creates and updates of all scenario entries and create a CreatedOrExisting record for every Entry
  for (const entry of allScenarioEntries) {
    if (isCargoEntry(entry) && !scenarioOrders.has(entry.orderId)) {
      // Don't commit changes to deleted orders
      continue;
    }
    if (!entryInScope(entry)) continue;
    const existing = committedEntriesByCId.get(entry.clientId);
    // SEABER-6624: for port-call entries specifically, only emit an UPDATE
    // patch when the current user touched the port call in this scenario
    // (flagged by the reducer as `EntryState.UPDATED`). Commit-time
    // `updateScenarioFromTimelines` re-derives every port call's planned
    // timestamps from inputs that can drift session-to-session (vessel
    // position, distances, weather, bunker prices). Without this filter
    // those drifts leaked phantom `UpdatePortCallById` mutations onto
    // untouched port calls, which the temporal_tables versioning trigger
    // then attributed to the saver. Other entry types (TC/COA/Spot/Single/
    // OffHire) still rely on the value-diff against committedData since
    // their cascades are tracked through the scenario reducer directly.
    const requireUserFlag = isPortCallEntry(entry);
    const isUserUpdated =
      scenario.entryStates.get(entry.clientId) === EntryState.UPDATED;
    const coe = existing
      ? requireUserFlag && !isUserUpdated
        ? { existing: existing.dbId }
        : updateEntry(result, entry, existing, deltas)
      : createEntry(result, entry, {
          currentOrgId: opts.currentUser.organizationId,
          currentUserId: opts.currentUser.id,
        });
    createdOrExistingByCId.set(entry.clientId, coe);

    if (isCargoEntry(entry)) {
      const existingAssignment = committedOrderAssignmentsByOrderId.get(
        entry.orderId,
      );
      if (
        existingAssignment &&
        existingAssignment.clientId !== entry.clientId
      ) {
        // Same order with different assignment (could be the same type tc/spot/coa or different ones, doesn't matter here)
        deleteEntry(
          result,
          existingAssignment,
          assignmentCleanupData,
          scenario,
          deletedCargoEntryOrderIds,
        );
      }
    }
  }

  // Handle deletes on all committed entries which are no longer present in scenario, and delete their port call relations.
  for (const entry of allCommittedEntries) {
    if (
      // Vessel-scoped saves never delete a master row outside the scope, even
      // one the user flagged DELETED — only in-scope (entangled) vessels move.
      entryInScope(entry) &&
      !scenarioEntriesByCId.has(entry.clientId) &&
      // On OVERRIDE, all missing entries will be deleted, with MERGE only delete those that the user has manually deleted in this scenario
      (policy === ScenarioCommitPolicy.OVERRIDE ||
        scenario.entryStates.get(entry.clientId) === EntryState.DELETED)
    ) {
      if (isPortCallEntry(entry)) {
        /** TODO: Enable deleting port calls when policies allow us to do port call cleanup from the frontend
         * Currently the client doesn't have access to port notices unless there's an order port call relation,
         * so we are not able to delete some port notices before port call, making port call deletion fail the commit.
         */
        // const fragment = assertedDefined(
        //   committed.portCallFragmentsByDbId.get(entry.dbId),
        //   `Expected committed port call entry ${entry.dbId}/${entry.clientId} to have its data fragment present`,
        // );
        // // Only clean up port call if it has no relations in database
        // if (
        //   !fragment.billOfLadingsByPortCallId?.nodes.length &&
        //   !fragment.portCallFilesByPortCallId?.nodes.length &&
        //   !fragment.invoiceItemsByPortCallId?.nodes.length &&
        //   !fragment.portNoticesByPortCallId?.nodes.length &&
        //   !fragment.statementOfFactsByPortCallId?.nodes.length &&
        //   /** We don't want to delete any port calls with relations to existing orders / single events.
        //    * As a side effect this will delay deletion of port calls for which we just deleted all assignments to the next commit.
        //    * The alternative would be to make this logic a bit more complicated:
        //    *  - process port calls after other events and ignore from this check those relations for which we are deleting order/SE relations in this same commit.
        //    */
        //   !fragment.orderPortCallRelationsByPortCallId?.nodes.length &&
        //   !fragment.singleEventPortCallRelationsByPortCallId?.nodes.length
        // ) {
        //   deleteEntry(result, entry, assignmentCleanupData);
        // }
      } else {
        deleteEntry(
          result,
          entry,
          assignmentCleanupData,
          scenario,
          deletedCargoEntryOrderIds,
        );
      }

      if (isCargoEntry(entry)) {
        const existingRels = committedData.orderPortCallRelationsByOrderId.get(
          entry.orderId,
        );

        for (const rel of existingRels.values()) {
          if (rel) {
            deleteOrderPortCallRelation(result, rel);
          }
        }
      } else if (isSingleEntry(entry)) {
        const rel =
          committedData.singleEventPortCallRelationsBySingleEventId.get(
            entry.dbId,
          );

        if (rel) {
          deleteSingleEventPortCallRelation(result, rel);
        }
      }
    }
  }

  // Port call relations handling. This needs to be after the previous loop where entries were created because everything needs to already exist, so we can refer to them in relations
  // Handle creations and changes related to port calls in current scenario
  for (const entry of allScenarioEntries) {
    if (!entryInScope(entry)) continue;
    if (isCargoEntry(entry)) {
      if (!scenarioOrders.has(entry.orderId)) {
        // Don't commit changes to deleted orders
        continue;
      }
      const { loadingPortCallId, dischargingPortCallId } = entry;
      const existingOPCRels = committedData.orderPortCallRelationsByOrderId.get(
        entry.orderId,
      );

      for (const [portCallId, isLoadPort] of [
        [loadingPortCallId, true],
        [dischargingPortCallId, false],
      ] as const) {
        const existingOPCRel = existingOPCRels.get(isLoadPort);
        const existingPC =
          existingOPCRel &&
          committedData.portCallEntriesByDbId.get(existingOPCRel.portCallId);

        if (existingPC?.clientId !== portCallId) {
          // Delete old Order-PC relation
          if (existingOPCRel) {
            deleteOrderPortCallRelation(result, existingOPCRel);
          }

          if (portCallId) {
            const pcId = createdOrExistingByCId.get(portCallId);
            if (!pcId) {
              console.warn(
                `Referenced port call does not exist for ${portCallId}, cannot create relation`,
              );
              continue;
            }
            // Create new Order-PC relation
            createOrderPortCallRelation(result, {
              orderId: entry.orderId,
              isLoadPort,
              portCallId: pcId,
            });
          }
        }
      }
    } else if (isSingleEntry(entry)) {
      const committedEntry = allSingleEventEntriesByClientId.get(
        entry.clientId,
      );
      const existingRel =
        committedEntry &&
        committedData.singleEventPortCallRelationsBySingleEventId.get(
          committedEntry.dbId,
        );
      const existingPCCId =
        existingRel &&
        committedData.portCallEntriesByDbId.get(existingRel.portCallId)
          ?.clientId;

      // Relation exists but wrong port call id -> delete
      if (existingRel && existingPCCId !== entry.portCallId) {
        deleteSingleEventPortCallRelation(result, existingRel);
      }
      // Event has port call but existing was wrong (got deleted) or didn't exist -> create
      if (entry.portCallId && entry.portCallId !== existingPCCId) {
        const seId = createdOrExistingByCId.get(entry.clientId);
        if (!seId) {
          console.warn(`Expected singleEvent to be created before relation`);
          continue;
        }
        const pcId = createdOrExistingByCId.get(entry.portCallId);
        if (!pcId) {
          console.warn(`Expected portCall to be created before relation`);
          continue;
        }
        createSingleEventPortCallRelation(result, {
          singleEventId: seId,
          portCallId: pcId,
        });
      }
    }
  }

  // New/updated shipments -->

  const createdShipments = new Map<ClientId, CreatedOrExisting>();

  for (const [shipmentId, shipment] of shipmentData.entries()) {
    if (!shipmentInScope(shipmentId)) continue;
    if (!shipment.id) {
      if (!shipment.name && !opts.forDryRun) {
        console.error(
          `Shipment ${shipmentId} has no generated name at commit. This is unexpected.`,
        );
      }
      (result.createShipments ??= []).push({
        name: shipment.name ?? `S-${shipmentId}`,
        userId: currentUser.id,
        chartererOrganisation: currentUser.organizationId,
        clientId: shipmentId,
        externalShipmentId: shipment.externalShipmentId,
        voyageAttributeId: shipment.voyageAttributeId,
      });
      createdShipments.set(shipmentId, {
        created: result.createShipments.length - 1,
      });
    } else {
      const existingShipment = shipmentByClientId.get(shipmentId);
      assertDefined(
        existingShipment,
        `Shipment ${shipmentId} has dbId ${shipment.id} but is not found in committed data`,
      );

      const hasExternalShipmentIdUpdated =
        existingShipment.externalShipmentId !== shipment.externalShipmentId;
      const hasVoyageAttributeIdUpdated =
        existingShipment.voyageAttributeId !== shipment.voyageAttributeId;
      const hasNameUpdated =
        shipment.name != null && existingShipment.name !== shipment.name;
      if (
        !(
          hasExternalShipmentIdUpdated ||
          hasVoyageAttributeIdUpdated ||
          hasNameUpdated
        )
      )
        continue;

      (result.updateShipments ??= []).push({
        id: existingShipment.id,
        shipmentPatch: {
          ...(hasExternalShipmentIdUpdated
            ? { externalShipmentId: shipment.externalShipmentId }
            : {}),
          ...(hasVoyageAttributeIdUpdated
            ? { voyageAttributeId: shipment.voyageAttributeId }
            : {}),
          ...(hasNameUpdated ? { name: shipment.name } : {}),
        },
      });
    }
  }

  // Shipment freights -->

  const usedScenarioShipmentsByClientId = new Set(
    usedScenarioShipments.map((shipment) => shipment.clientId),
  );

  for (const [
    shipmentClientId,
    shipmentFreight,
  ] of scenario.freightByShipmentClientId) {
    if (!shipmentInScope(shipmentClientId)) continue;
    const shipment = committedData.shipmentByClientId.get(shipmentClientId);
    const existingShipmentFreight = shipment?.shipmentFreightByShipmentId;
    // Create new freight
    if (!existingShipmentFreight) {
      const committedShipmentId = shipment?.id;
      const shipmentId = committedShipmentId
        ? {
            existing: committedShipmentId,
          }
        : createdShipments.get(shipmentClientId);
      if (!shipmentId) {
        console.warn(
          `Shipment ${shipmentClientId} has no id, cannot create freight`,
        );
        continue;
      }
      (result.createShipmentFreights ??= []).push({
        shipmentId,
        freightAmount: shipmentFreight.freightAmount,
        freightUnit: shipmentFreight.freightUnit,
        clientId: shipmentFreight.clientId,
      });
    }
    // Update or delete existing freight
    else if (
      existingShipmentFreight &&
      // Ignore update freight if the shipment has been deleted
      usedScenarioShipmentsByClientId.has(shipmentClientId)
    ) {
      if (
        Number(existingShipmentFreight.freightAmount) !==
          Number(shipmentFreight.freightAmount) ||
        existingShipmentFreight.freightUnit !== shipmentFreight.freightUnit
      ) {
        (result.updateShipmentFreights ??= []).push({
          id: existingShipmentFreight.id,
          shipmentFreightPatch: {
            freightAmount: shipmentFreight.freightAmount,
            freightUnit: shipmentFreight.freightUnit,
          },
        });
      }
    }
  }

  for (const [
    shipmentClientId,
    shipmentExtraFreights,
  ] of scenario.extraFreightByShipmentClientId) {
    if (!shipmentInScope(shipmentClientId)) continue;
    const committedShipmentId =
      committedData.shipmentByClientId.get(shipmentClientId)?.id;
    const shipment = committedData.shipmentByClientId.get(shipmentClientId);
    const shipmentId = committedShipmentId
      ? {
          existing: committedShipmentId,
        }
      : createdShipments.get(shipmentClientId);

    const committedShipmentExtraFreights =
      shipment?.shipmentExtraFreightsByShipmentId ?? [];

    const allExtraFreightClientIds = new Set<string>();
    for (const extraFreight of shipmentExtraFreights) {
      allExtraFreightClientIds.add(parseClientId(extraFreight.clientId));

      const existingExtraFreight = committedShipmentExtraFreights.find(
        (ef) =>
          parseClientId(ef.clientId) === parseClientId(extraFreight.clientId),
      );

      if (existingExtraFreight) {
        if (
          Number(existingExtraFreight.freightAmount) !==
            Number(extraFreight.freightAmount) ||
          existingExtraFreight.freightUnit !== extraFreight.freightUnit ||
          existingExtraFreight.name !== extraFreight.name ||
          existingExtraFreight.description !== extraFreight.description
        ) {
          (result.updateShipmentExtraFreights ??= []).push({
            id: existingExtraFreight.id,
            shipmentExtraFreightPatch: {
              freightAmount: extraFreight.freightAmount,
              freightUnit:
                extraFreight.freightUnit.toUpperCase() as PaymentUnit,
              name: extraFreight.name,
              description: extraFreight.description,
            },
          });
        }
      } else if (
        shipmentId &&
        !committedShipmentExtraFreights.find(
          (ef) => ef.clientId === extraFreight.clientId,
        )
      ) {
        (result.createShipmentExtraFreights ??= []).push({
          shipmentId,
          freightAmount: extraFreight.freightAmount,
          freightUnit: extraFreight.freightUnit,
          name: extraFreight.name,
          description: extraFreight.description,
          clientId: extraFreight.clientId,
        });
      }
    }
  }

  for (const [
    shipmentClientId,
    {
      shipmentFreightByShipmentId: existingFreight,
      shipmentExtraFreightsByShipmentId: existingExtraFreights,
    },
  ] of committedData.shipmentByClientId) {
    // A scoped save must not touch freight on out-of-scope shipments; those
    // changes stay pending like every other out-of-scope edit.
    if (!shipmentInScope(shipmentClientId)) continue;
    const shipmentFreight =
      scenario.freightByShipmentClientId.get(shipmentClientId);
    const shipmentExtraFreights =
      scenario.extraFreightByShipmentClientId.get(shipmentClientId) ?? [];

    if (!shipmentFreight && existingFreight) {
      (result.deleteShipmentFreights ??= []).push(existingFreight.id);
    }

    existingExtraFreights?.forEach((existingExtraFreight) => {
      const shipmentExtraFreight = shipmentExtraFreights.find(
        (ef) => ef.clientId === existingExtraFreight.clientId,
      );
      if (!shipmentExtraFreight) {
        (result.deleteShipmentExtraFreights ??= []).push(
          existingExtraFreight.id,
        );
      }
    });
  }

  // Cargo compartment allocations
  const createCargoCompartmentAllocations: CommitCargoCompartmentAllocationInput[] =
    [];
  const updateCargoCompartmentAllocations: UpdateCargoCompartmentAllocationByIdInput[] =
    [];
  const allAllocationClientIds = new Set();
  for (const allocation of scenario.cargoAllocations ?? []) {
    const clientId = allocation.clientId;
    allAllocationClientIds.add(clientId);
    // Allocations reach a vessel through their order; out-of-scope ones are skipped.
    if (scopeVesselIds && !orderInScope(allocation.orderId)) continue;
    const existingAllocation = committedData.cargoAllocations.get(clientId);
    if (existingAllocation) {
      const amount = String(allocation.amount);
      if (existingAllocation.amount !== amount) {
        updateCargoCompartmentAllocations.push({
          id: existingAllocation.dbId,
          cargoCompartmentAllocationPatch: {
            amount,
          },
        });
      }
    } else {
      const input = cargoAllocationEntryToInput(
        allocation,
        createdOrExistingByCId,
      );
      if (!input) {
        console.warn(`Expected scheduledOrder to be created before allocation`);
        continue;
      }
      createCargoCompartmentAllocations.push(input);
    }
  }
  if (createCargoCompartmentAllocations.length) {
    result.createCargoCompartmentAllocations =
      createCargoCompartmentAllocations;
  }
  if (updateCargoCompartmentAllocations.length) {
    result.updateCargoCompartmentAllocations =
      updateCargoCompartmentAllocations;
  }
  for (const existingAllocation of committedData.cargoAllocations.values()) {
    if (scopeVesselIds && !orderInScope(existingAllocation.orderId)) continue;
    if (
      (policy === ScenarioCommitPolicy.OVERRIDE &&
        !allAllocationClientIds.has(existingAllocation.clientId)) ||
      scenario.entryStates.get(existingAllocation.scheduledOrderClientId) ===
        EntryState.DELETED
    ) {
      pushIfNotExist(
        (result.deleteCargoCompartmentAllocations ??= []),
        existingAllocation.dbId,
      );
    }
  }

  // Order updates -->
  const { scenarioOrderById: committedScenarioOrders } =
    getScenarioOrdersAndShipments(
      committedOrderById,
      shipmentByClientId,
      {
        patchOrders: new Map(),
        patchShipments: new Map(),
        cargoAllocations: [...cargoAllocations.values()],
        integrationData: {},
      },
      {
        ignoreIntegrationCargoMargins:
          !!currentUser.organization?.configIgnoreIntegrationCargoMargins,
      },
    );

  for (const [orderId, { patch }] of scenario.patchOrders) {
    // Patch an order when it is assigned to an in-scope vessel, or unassigned
    // (no vessel to scope by — always saved per spec).
    if (
      scopeVesselIds &&
      !orderInScope(orderId) &&
      !orderHasNoVessel(orderId)
    ) {
      continue;
    }
    const order = committedScenarioOrders.get(orderId);
    if (!order) {
      console.error(
        `Order ${orderId} to patch was not found. This is not fatal but probably some cleanup is missing.`,
        { orderId, patch },
      );
      continue;
    }

    const primitivesPatch: Partial<ScenarioOrderPatch['patch']> = omitBy(
      patch,
      (v, k) => isObject(v) || k === 'shipmentId', // shipmentId requires special handling as a DB id and ClientId may refer to same shipment
    );
    // If any updated relations need handling, do it here with this:
    // const relationsPatch = pickBy(patch, isObject);

    // If any values are already identical they don't need to be included in the update. This may save some unnecessary calls but isn't a necessary check.
    for (const k of Object.keys(
      primitivesPatch,
    ) as (keyof typeof primitivesPatch)[]) {
      if (order[k] === primitivesPatch[k]) {
        delete primitivesPatch[k];
      }
    }

    const patchShipmentCId = patch.shipmentId;
    const orderShipmentCId = order.shipmentId;

    let shipmentIdCoE: CreatedOrExisting | null | undefined = undefined;

    if (
      patchShipmentCId !== undefined &&
      patchShipmentCId !== orderShipmentCId
    ) {
      if (patchShipmentCId === null) {
        shipmentIdCoE = null;
      } else {
        const created = createdShipments.get(patchShipmentCId);
        const existing = shipmentByClientId.get(patchShipmentCId);

        shipmentIdCoE = existing
          ? {
              existing: existing.id,
            }
          : created;
      }

      if (shipmentIdCoE === undefined) {
        console.error(
          `Order ${order.id} has patched shipmentId ${shipmentIdCoE}, but no matching shipment was created or existing`,
          orderId,
          primitivesPatch,
          createdShipments,
        );
        throw new Error('Error creating new shipments');
      }
    }

    // If there are no fields to patch, no need to do anything
    if (
      Object.keys(primitivesPatch).length === 0 &&
      shipmentIdCoE === undefined
    ) {
      continue;
    }

    (result.updateOrders ??= []).push({
      id: orderId,
      orderPatch: {
        ...primitivesPatch,
        shipmentId: shipmentIdCoE,
      },
    });
  }

  // Voyage charter and coa contract stuff ->
  if (voyageCharterData) {
    const ordersByVoyageCharterId = new Map<
      VoyageCharterId | string,
      ScenarioOrder[]
    >();

    for (const [orderId, voyageCharterId] of scenario.voyageCharterByOrderId) {
      if (voyageCharterId === null) continue;
      if (scopeVesselIds && !orderInScope(orderId)) continue;

      const currentOrders = ordersByVoyageCharterId.get(voyageCharterId) ?? [];
      const order = scenarioOrders.get(orderId);
      if (!order) {
        // Order has been deleted since creation of the voyage charter in scenario
        console.log(`Order ${orderId} has been deleted but has data for vc`);
        continue;
      }

      ordersByVoyageCharterId.set(voyageCharterId, [...currentOrders, order]);
    }
    const coaSpotPortCallsByOrderId = new DefaultMap<
      OrderId,
      CreatedOrExisting[]
    >(() => []);
    for (const entry of [
      ...scenario.coaAssignments,
      ...scenario.spotAssignments,
    ]) {
      if (scopeVesselIds && !orderInScope(entry.orderId)) continue;
      for (const portCallId of [
        entry.loadingPortCallId,
        entry.dischargingPortCallId,
      ]) {
        if (portCallId !== null) {
          const portCallCOE = assertedDefined(
            createdOrExistingByCId.get(portCallId),
            `${entry.category} entry refers to a port call ${portCallId} which isn't present in the scenario`,
          );
          coaSpotPortCallsByOrderId.get(entry.orderId).push(portCallCOE);
        }
      }
    }

    const createOrUpdateVoyageCharterMutators: CreateOrUpdateVoyageCharterArgs =
      {
        createVoyageCharter: (input) => {
          (result.createVoyageCharters ??= []).push(input);
          return created(result.createVoyageCharters);
        },
        updateVoyageCharter: (input) => {
          (result.updateVoyageCharters ??= []).push(input);
        },
        createOrderRel: (input) => {
          (result.createOrderVoyageCharterRelations ??= []).push(input);
          return created(result.createOrderVoyageCharterRelations);
        },
        deleteOrderRel: (input) => {
          pushIfNotExist(
            (result.deleteOrderVoyageCharterRelations ??= []),
            input.id,
          );
        },
        createVoyageCharterOrderFreight: (input) => {
          (result.createVoyageCharterOrderFreights ??= []).push(input);
          return created(result.createVoyageCharterOrderFreights);
        },
        updateVoyageCharterOrderFreight: (input) => {
          (result.updateVoyageCharterOrderFreights ??= []).push(input);
        },
        createContractRel: (input) => {
          (result.createVoyageCharterToContracts ??= []).push(input);
          return created(result.createVoyageCharterToContracts);
        },
        updateContractRel: (input) => {
          (result.updateVoyageCharterToContracts ??= []).push(input);
        },
        deleteContractRel: (input) => {
          pushIfNotExist(
            (result.deleteVoyageCharterToContracts ??= []),
            input.id,
          );
        },
        createPortDataRel: (input) => {
          (result.createVoyageCharterPortData ??= []).push(input);
          return created(result.createVoyageCharterPortData);
        },
        deletePortDataRel: (input) => {
          pushIfNotExist((result.deleteVoyageCharterPortData ??= []), input.id);
        },
      };

    // Implementation note: lazily copied the original voyage charter creation and replaced graphql mutations with "mutators" which operate on the CommitInput
    // This should help prevent any regressions from completely rewriting the method, especially with tests still lacking on this part.
    for (const [voyageCharterId, orders] of ordersByVoyageCharterId) {
      const orderIds = new Set(orders.map((o) => o.id));

      const anyAssignment =
        scenario.spotAssignments.find((assignment) =>
          orderIds.has(assignment.orderId),
        ) ??
        scenario.coaAssignments.find((assignment) =>
          orderIds.has(assignment.orderId),
        );

      if (!anyAssignment) {
        throw new Error(
          `No assignment found for voyage charter ${voyageCharterId}`,
        );
      }

      const spotShipownerOrgId =
        anyAssignment.category === EventCategory.Spot
          ? anyAssignment.ownerOrganisationId
          : null;

      createOrUpdateVoyageCharter(
        orders,
        currentUser.organizationId,
        currentUser.id,
        anyAssignment.vesselId ?? null,
        typeof voyageCharterId === 'number' ? voyageCharterId : null,
        spotShipownerOrgId ?? null,
        anyAssignment.category === EventCategory.COA
          ? anyAssignment.coaId
          : null,
        coaSpotPortCallsByOrderId,
        voyageCharterData.coasById,
        voyageCharterData.voyageChartersById,
        createOrUpdateVoyageCharterMutators,
        result,
      );
    }
  }

  let committedEntryClientIds: Set<ClientId> | null = null;
  if (scopeVesselIds) {
    // In-scope entries still present (created/updated) plus in-scope rows the
    // user deleted — the latter are gone from the scenario but committed as
    // deletes (see the delete loop above), so their DELETED flag must clear too.
    committedEntryClientIds = new Set<ClientId>(
      allScenarioEntries.filter(entryInScope).map((e) => e.clientId),
    );
    for (const entry of allCommittedEntries) {
      if (
        entryInScope(entry) &&
        !scenarioEntriesByCId.has(entry.clientId) &&
        scenario.entryStates.get(entry.clientId) === EntryState.DELETED
      ) {
        committedEntryClientIds.add(entry.clientId);
      }
    }
  }

  return { commit: result, deltas, committedEntryClientIds };
}

const isCreated = (
  coe: CreatedOrExisting,
): coe is CreatedOrExisting & { created: number } =>
  typeof coe.created === 'number';

/** Applies changes of id's, relations and states and such which may have relevant changes as a result of commit.
 * Note: maybe we shouldn't hold persistence related information in scenario though as it may always be out of date vs database. */
export function updateScenarioFromCommitResult<T extends UserSchedule>(
  scenario: T,
  input: CommitInput,
  result: CommitMutationResult['data'],
  committedScenario: T,
  committedEntryClientIds: ReadonlySet<ClientId> | null,
): T {
  const newOrderPatches = new Map(scenario.patchOrders);
  for (const patch of scenario.patchOrders.values()) {
    // The committed scenario has more accurate info from merged/updated order patches
    const orderId = patch.id;
    const committedPatch = committedScenario.patchOrders.get(orderId);
    if (committedPatch) {
      newOrderPatches.set(orderId, committedPatch);
    }
  }

  const voyageCharterByOrderId = new Map(scenario.voyageCharterByOrderId);
  for (const orderVoyageCharterRelation of input.createOrderVoyageCharterRelations ??
    []) {
    const orderId = orderVoyageCharterRelation.orderId;
    const vcIdCoE = orderVoyageCharterRelation.voyageCharterId;
    if (vcIdCoE != null) {
      const vcDbId = isCreated(vcIdCoE)
        ? result?.commit?.createdVoyageCharters?.[vcIdCoE.created]?.id
        : vcIdCoE.existing;
      if (typeof vcDbId !== 'number') {
        throw Error('Missing voyage charter id' + JSON.stringify(vcIdCoE));
      }
      voyageCharterByOrderId.set(orderId as OrderId, vcDbId as VoyageCharterId);
    }
  }

  return {
    ...scenario,
    patchOrders: newOrderPatches,
    voyageCharterByOrderId,
    // A scoped commit only wrote the in-scope entries to main, so clear just
    // their change-tracking; out-of-scope edits stay pending on the scenario
    // (vessel-scoped-commits spec). An all-vessels commit (`null`) clears all.
    entryStates: committedEntryClientIds
      ? mapFilter(
          scenario.entryStates,
          (_state, clientId) => !committedEntryClientIds.has(clientId),
        )
      : new Map(),
  };
}

export async function fetchVoyageCharterDataForCommit(
  client: ApolloClient<unknown>,
  coaIds: number[],
  voyageCharterIds: number[],
) {
  const result =
    coaIds.length === 0 && voyageCharterIds.length === 0
      ? undefined
      : await client.query<
          VoyageCharterDataForCommitQueryResult['data'],
          VoyageCharterDataForCommitQueryVariables
        >({
          query: VoyageCharterDataForCommitDocument,
          variables: {
            coaIds,
            voyageCharterIds,
          },
          fetchPolicy: 'no-cache',
        });
  const error = result?.error;
  if (error) {
    throw error;
  }

  const data = result?.data;
  const coasById = mapKeyBy(compact(data?.allCoaContracts?.nodes), (x) => x.id);
  const voyageChartersById = mapKeyBy(
    compact(data?.allVoyageCharters?.nodes),
    (x) => x.id,
  );

  return {
    coasById,
    voyageChartersById,
  };
}

export async function fetchAssignmentCleanupData(
  client: ApolloClient<unknown>,
  deletedCargoEntryOrderIds: Set<number>,
) {
  const deletedAssignmentCleanupData = await client.query<
    GetDataToCleanupForAllOrdersQueryResult['data'],
    GetDataToCleanupForAllOrdersQueryVariables
  >({
    query: GetDataToCleanupForAllOrdersDocument,
    variables: { ids: [...deletedCargoEntryOrderIds] },
    fetchPolicy: 'no-cache',
  });

  if (!deletedAssignmentCleanupData.data) {
    const msg = `Could not fetch data for deleted assignments clean up, cannot save to main.`;
    toast.error(msg);
    console.error(msg);
    throw new Error(msg);
  }

  return new Map(
    compact(deletedAssignmentCleanupData.data.allOrders?.nodes).map((o) => [
      o.id,
      o,
    ]),
  );
}

export type UnwrapPromise<T> = T extends Promise<infer U> ? U : never;

export type VoyageCharterDataForCommit = UnwrapPromise<
  ReturnType<typeof fetchVoyageCharterDataForCommit>
>;

export type AssignmentCleanupDataForCommit = UnwrapPromise<
  ReturnType<typeof fetchAssignmentCleanupData>
>;

export function parseVoyageCharterDataForCommit({
  coasById,
  voyageChartersById,
}: VoyageCharterDataForCommit) {
  const voyageCharterRelationsByOrderId = mapGroupBy(
    [...voyageChartersById.values()].flatMap((x) =>
      compact(x.orderVoyageCharterRelationsByVoyageCharterId.nodes),
    ),
    (x) => x.orderId,
  );
  return {
    coasById,
    voyageChartersById,
    voyageCharterRelationsByOrderId,
  };
}

export type ParsedVoyageCharterDataForCommit = ReturnType<
  typeof parseVoyageCharterDataForCommit
>;

export type ShipmentDataForCommit = Map<ClientId, ScenarioShipment>;

export function getShipmentPrefix(
  orders: ScenarioOrder[],
  allScenarioOrderAssignments: Map<number, CargoEntry>,
) {
  // Released (unassigned) orders contribute no port/timing to a voyage name —
  // skip them rather than crashing the whole save. Callers must ensure at
  // least one order is assigned (see applyShipmentNames).
  const ordersAndAssignments = orders.flatMap((order) => {
    const assignment = allScenarioOrderAssignments.get(order.id);
    return assignment ? [{ order, assignment }] : [];
  });

  const shipmentNamePortsPart = ordersAndAssignments
    .flatMap(({ order, assignment }) => {
      return [
        {
          country: order.locationByLoadPortId?.countryByCountryId?.alpha2,
          locode: order.locationByLoadPortId?.locode,
          name: order.locationByLoadPortId?.name,
          timestamp: assignment.loadingStart,
          locationId: order.loadPortId,
        },
        {
          country: order.locationByDischargePortId?.countryByCountryId?.alpha2,
          locode: order.locationByDischargePortId?.locode,
          name: order.locationByDischargePortId?.name,
          timestamp: assignment.dischargingStart,
          locationId: order.dischargePortId,
        },
      ];
    })
    .sort((a, b) => a.timestamp - b.timestamp)
    .filter(
      // Remove duplicate entries which follow each other
      (value, index, array) =>
        index === array.length - 1
          ? true
          : value.locationId !== array[index + 1].locationId,
    )
    .map(
      (port) => `${port.country || '??'}${port.locode || port.name || '???'}`,
    )
    .join('-');

  const earliestLoadingEndTimestamp = Math.min(
    ...ordersAndAssignments.map(({ assignment }) => assignment.loadingEnd),
  );
  if (earliestLoadingEndTimestamp === Infinity) {
    throw new Error(`Unable to determine earliest loading end timestamp`);
  }

  return {
    prefix:
      shipmentNamePortsPart +
      '-' +
      new Date(earliestLoadingEndTimestamp).getFullYear(),
    zerosPad: null,
    separator: '-',
  };
}

export type { VoyageNumberingReset };

export const getShipmentPrefixWithRule = (
  namingRule: Omit<
    VoyageNamingRule,
    'shipVoyageNamingRuleRelationsByRuleId' | 'nodeId'
  >,
  vessel?: Vessel | ScenarioTimeline_ShipFragment,
  orders?: ScenarioOrder[],
  allScenarioOrderAssignments?: Map<number, CargoEntry>,
  resets: readonly VoyageNumberingReset[] = [],
): FetchShipmentPrefixInput => {
  const rule: VoyageNameRule = {
    prefix: namingRule.prefix ?? null,
    separator: namingRule.separator ?? '',
    year: normalizeYearFormat(namingRule.year),
    zerosPad: namingRule.zerosPad,
    vesselFullname: namingRule.vesselFullname,
    vesselShortname: namingRule.vesselShortname,
    vesselImo: namingRule.vesselImo,
  };
  const nameVessel: VoyageNameVessel = {
    fullName: vessel?.name ?? null,
    shortName: vessel?.shortName ?? null,
    imo: vessel?.imo ?? null,
  };

  let yearValue: number;
  if (orders && allScenarioOrderAssignments && orders.length > 0) {
    const loadingEndTimestamps = orders
      .map((o) => allScenarioOrderAssignments.get(o.id)?.loadingEnd)
      .filter(isDefined);
    yearValue =
      loadingEndTimestamps.length > 0
        ? resolveYear(Math.min(...loadingEndTimestamps), buildResetMap(resets))
        : new Date().getUTCFullYear();
  } else {
    yearValue = new Date().getUTCFullYear();
  }

  return {
    prefix: formatVoyagePrefix(rule, nameVessel, yearValue),
    zerosPad: rule.zerosPad,
    separator: rule.separator,
    scope: vessel?.id != null ? `v${vessel.id}_` : undefined,
  };
};

export async function fetchShipmentNames(
  client: ApolloClient<unknown>,
  shipmentPrefixesInput: FetchShipmentPrefixInput[],
) {
  if (shipmentPrefixesInput.length === 0) {
    return [];
  }
  const shipmentNameQueryResults = await client.mutate<
    VoyagePrefixGetNextIdsMutationResult['data'],
    VoyagePrefixGetNextIdsMutationVariables
  >({
    mutation: VoyagePrefixGetNextIdsDocument,
    variables: {
      input: {
        prefixObjects: shipmentPrefixesInput.map((a) => JSON.stringify(a)),
      },
    },
    fetchPolicy: 'no-cache',
  });

  const shipmentNames =
    shipmentNameQueryResults.data?.voyagePrefixGetNextIds?.strings;

  if (!shipmentNames) {
    throw new Error(`Could not determine shipment names`);
  }

  return shipmentNames;
}

export interface FetchShipmentPrefixInput {
  prefix: string;
  zerosPad: number | null;
  separator: string | null;
  scope?: string;
}

/** Returns clientIds of persisted shipments whose vessel assignment has
 * changed since last commit — i.e. at least one of their orders was moved
 * to a different vessel (or from a vessel to a spot timeline, or vice versa). */
export const getShipmentsWithVesselChange = (
  usedShipments: ScenarioShipment[],
  ordersByShipmentId: Map<ClientId | null, ScenarioOrderWithShipmentId[]>,
  currentVesselByOrderId: Map<number, number | null>,
  committedVesselByOrderId: Map<number, number | null>,
): Set<ClientId> => {
  const result = new Set<ClientId>();
  for (const shipment of usedShipments) {
    if (shipment.id === null) continue;
    const orders = ordersByShipmentId.get(shipment.clientId);
    if (!orders) continue;
    for (const order of orders) {
      if (!committedVesselByOrderId.has(order.id)) continue;
      const prevVessel = committedVesselByOrderId.get(order.id);
      const currVessel = currentVesselByOrderId.get(order.id);
      if (prevVessel !== currVessel) {
        result.add(shipment.clientId);
        break;
      }
    }
  }
  return result;
};

/**
 * Expand a requested set of vessel ids to every vessel transitively entangled
 * with it by a cargo reassignment, and report which vessels were drawn in.
 *
 * Two vessels are entangled when some order's committed (master) vessel and its
 * current (scenario) vessel differ and both are real vessels. Saving only one
 * of them commits half the move and leaves master inconsistent: the other
 * vessel's schedule (its port calls and timings, freed up or taken on by the
 * move) would not be recomputed and committed. See the vessel-scoped commits
 * spec, "Affected Vessels". The relation is symmetric and transitive, so the
 * result is the connected component(s) of the requested vessels.
 *
 * `affected` is `expandedScope \ requestedScope` — the vessels the user must be
 * told about and accept before the scoped save proceeds.
 */
export function computeScopeClosure(
  requestedScope: ReadonlySet<number>,
  committedVesselByOrderId: ReadonlyMap<number, number | null>,
  currentVesselByOrderId: ReadonlyMap<number, number | null>,
): { expandedScope: Set<number>; affected: Set<number> } {
  const neighbours = new Map<number, Set<number>>();
  const link = (a: number, b: number): void => {
    const existing = neighbours.get(a);
    if (existing) {
      existing.add(b);
    } else {
      neighbours.set(a, new Set([b]));
    }
  };

  const orderIds = new Set<number>([
    ...committedVesselByOrderId.keys(),
    ...currentVesselByOrderId.keys(),
  ]);
  for (const orderId of orderIds) {
    const masterVessel = committedVesselByOrderId.get(orderId) ?? null;
    const scenarioVessel = currentVesselByOrderId.get(orderId) ?? null;
    if (
      masterVessel !== null &&
      scenarioVessel !== null &&
      masterVessel !== scenarioVessel
    ) {
      link(masterVessel, scenarioVessel);
      link(scenarioVessel, masterVessel);
    }
  }

  const expandedScope = new Set<number>(requestedScope);
  const queue = [...requestedScope];
  while (queue.length > 0) {
    const vessel = queue.pop();
    if (vessel === undefined) break;
    for (const neighbour of neighbours.get(vessel) ?? []) {
      if (!expandedScope.has(neighbour)) {
        expandedScope.add(neighbour);
        queue.push(neighbour);
      }
    }
  }

  const affected = new Set<number>();
  for (const vessel of expandedScope) {
    if (!requestedScope.has(vessel)) {
      affected.add(vessel);
    }
  }

  return { expandedScope, affected };
}

/** Generate names for non-persisted shipments, and regenerate names for
 * persisted shipments listed in `shipmentsToRegenerate` (typically those
 * whose vessel assignment changed since the last commit). */
export async function applyShipmentNames(
  client: ApolloClient<unknown>,
  allScenarioOrderAssignments: Map<number, CargoEntry>,
  vesselsById: Map<number, Vessel>,
  voyageNamingRuleByShipId: Map<
    number,
    {
      id: ShipVoyageNamingRuleRelationId;
      rule_id: VoyageNamingRuleId;
      rule: VoyageNamingRule_AdminFragment;
    }
  >,
  noSideEffects: boolean,
  ordersByShipmentId: Map<ClientId | null, ScenarioOrderWithShipmentId[]>,
  usedShipments: ScenarioShipment[],
  voyageNumberingResetsByShipId: Map<number, VoyageNumberingReset[]>,
  shipmentsToRegenerate?: Set<ClientId>,
): Promise<ShipmentDataForCommit> {
  // In preview mode the placeholder name is the clientId; skip persisted
  // shipments to avoid surfacing UUIDs in the preview UI.
  const shipmentIdsNeedingNames = filteredMap(usedShipments, (shipment) => {
    // Manually-named voyages keep their freetext name and consume no counter.
    if (shipment.isManualNameOverride) return NONE;
    const needsName =
      shipment.id === null ||
      (!noSideEffects && shipmentsToRegenerate?.has(shipment.clientId));
    if (!needsName) return NONE;
    // A shipment whose orders are all unassigned (e.g. released) has no voyage
    // to name — keep its existing name rather than regenerating from nothing.
    const shipmentOrders = ordersByShipmentId.get(shipment.clientId) ?? [];
    if (!shipmentOrders.some((o) => allScenarioOrderAssignments.has(o.id))) {
      return NONE;
    }
    // Skip persisted shipments split across vessels; keep the existing name.
    if (shipment.id != null) {
      const orderVessels = uniq(
        (ordersByShipmentId.get(shipment.clientId) ?? [])
          .map((o) => allScenarioOrderAssignments.get(o.id)?.vesselId)
          .filter(isDefined),
      );
      if (orderVessels.length > 1) return NONE;
    }
    return shipment.clientId;
  });

  const shipmentPrefixes = shipmentIdsNeedingNames.map((shipmentId) => {
    const orders = ordersByShipmentId.get(shipmentId);
    assertDefined(
      orders,
      `Orders to patch for shipment ${shipmentId} not found`,
    );

    const uniqVesselIds = uniq(
      orders
        .map((o) => allScenarioOrderAssignments.get(o.id)?.vesselId)
        .filter(isDefined),
    );

    const vesselId = uniqVesselIds.at(0);
    if (!vesselId) {
      // There is no vessel on spot timeline
      return getShipmentPrefix(orders, allScenarioOrderAssignments);
    }

    if (uniqVesselIds.length !== 1) {
      throw new Error('Expect orders to be assigned in the same vessel');
    }

    assertDefined(vesselId, 'Undefined vesselId');

    const namingRule = voyageNamingRuleByShipId.get(vesselId);
    if (namingRule) {
      const vessel = vesselsById.get(vesselId);
      assertDefined(vessel, 'Could not find vessel');
      return getShipmentPrefixWithRule(
        namingRule.rule,
        vessel,
        orders,
        allScenarioOrderAssignments,
        voyageNumberingResetsByShipId.get(vesselId) ?? [],
      );
    }

    return getShipmentPrefix(orders, allScenarioOrderAssignments);
  });

  const shipmentNames = noSideEffects
    ? shipmentIdsNeedingNames
    : await fetchShipmentNames(client, shipmentPrefixes);

  const newShipmentNameById = new Map<ClientId, string>(
    shipmentIdsNeedingNames.map((shipmentId, i) => {
      const shipmentName = shipmentNames[i];
      if (!shipmentName) {
        throw new Error(
          `Could not determine shipment name for shipment with prefix ${shipmentPrefixes[i]}`,
        );
      }
      return [shipmentId, shipmentName];
    }),
  );

  return new Map(
    usedShipments.map((shipment) => {
      const newName = newShipmentNameById.get(shipment.clientId);
      return [
        shipment.clientId,
        !newName
          ? shipment
          : {
              ...shipment,
              name: newName,
            },
      ];
    }),
  );
}

export type BuildCommitResult = {
  commit: CommitInput;
  deltas: [Entry, Partial<Entry>][];
  updatedScenario: InternalUserSchedule;
  hadArchivedItems: boolean;
  /**
   * Out-of-scope vessels pulled into a scoped save because a reassignment
   * entangles them with the requested scope. Empty for all-vessels saves. The
   * UI must name these and get explicit acceptance before saving.
   */
  affectedVesselIds: Set<number>;
  /** See `createCommitInput`'s return — committed in-scope entries (`null` for all-vessels). */
  committedEntryClientIds: Set<ClientId> | null;
};

export function parseArchivedData(data: ArchivedDataQuery): ArchivedState {
  return <ArchivedState>{
    archivedItems: {
      orders: new Set(compact(data.allOrders?.nodes).map((o) => o.id)),
      [EventCategory.Single]: new Set(
        compact(data.allSingleEvents?.nodes).map((o) =>
          parseClientId(o.clientId),
        ),
      ),
      [EventCategory.OffHire]: new Set(
        compact(data.allOffHires?.nodes).map((o) => parseClientId(o.clientId)),
      ),
      [EventCategory.PortCall]: new Set(
        compact(data.allPortCalls?.nodes).map((o) => parseClientId(o.clientId)),
      ),
    },
  };
}

/** Clean up patchOrders map from patches to orders which don't exist anymore. */
function cleanupMissingOrdersFromScenario(
  scenario: InternalUserSchedule,
  orderById: Map<number, ScenarioTimeline_OrderFragment>,
): InternalUserSchedule {
  return {
    ...scenario,
    patchOrders: new Map(
      [...scenario.patchOrders].filter(([orderId]) => orderById.has(orderId)),
    ),
  };
}

/**
 * From the shipments that currently hold the target external ids, returns the ones
 * that genuinely conflict — excluding any shipment we are ourselves (re)assigning
 * that external id to. A shipment re-asserting its own External Voyage ID is not a
 * conflict with itself; a different shipment holding the same id still is. SEABER-6746.
 */
export function selectConflictingExternalIdShipments<T extends { id: number }>(
  shipmentsHoldingExternalIds: T[],
  reassignedShipmentIds: Set<number>,
): T[] {
  return shipmentsHoldingExternalIds.filter(
    (shipment) => !reassignedShipmentIds.has(shipment.id),
  );
}

/**
 * This function was introduces due to issue with IMOS scenarios
 * It is not a proper fix so TODO remove it
 */
async function cleanupConflictingShipmentsExternalIdsInTheDatabase(
  client: ApolloClient<unknown>,
  createShipmentsExternalIds: string[] | undefined,
  updateShipmentsExternalIds:
    | {
        externalShipmentId: string;
        shipmentId: number;
      }[]
    | undefined,
) {
  if (!createShipmentsExternalIds && !updateShipmentsExternalIds) {
    return;
  }
  const conflictingShipmentsResult = await client.query<
    ShipmentsByExternalIdQueryResult['data'],
    ShipmentsByExternalIdQueryVariables
  >({
    query: ShipmentsByExternalIdDocument,
    variables: {
      externalIds: [
        ...(createShipmentsExternalIds || []),
        ...(updateShipmentsExternalIds?.map(
          (shipment) => shipment.externalShipmentId,
        ) || []),
      ],
    },
    fetchPolicy: 'no-cache',
  });

  // A shipment we are ourselves updating to own one of these external ids is not
  // a conflict with itself. Excluding it prevents a false "commit would reuse
  // external shipment id" error when re-asserting the same external id on commit
  // (e.g. moving the performing vessel of a ShipNet-linked, reused shipment).
  const updatedShipmentIds = new Set(
    (updateShipmentsExternalIds ?? []).map((shipment) => shipment.shipmentId),
  );
  const conflictingShipments = selectConflictingExternalIdShipments(
    conflictingShipmentsResult?.data?.allShipments?.nodes?.filter(isDefined) ??
      [],
    updatedShipmentIds,
  );
  if (conflictingShipments.length === 0) {
    return;
  }
  if (conflictingShipments.some((s) => s.ordersByShipmentId.totalCount)) {
    throw new Error(
      'Inconsistent data, commit would reuse external shipment id. Please contact support@seaber.io',
    );
  }
  for (const conflictingShipment of conflictingShipments) {
    console.warn(
      `Removing conflicting shipmentExternalId '${conflictingShipment.externalShipmentId}' id=${conflictingShipment.id}`,
    );
    await client.mutate<
      UpdateShipmentMutationResult['data'],
      UpdateShipmentMutationVariables
    >({
      mutation: UpdateShipmentDocument,
      variables: {
        shipmentId: conflictingShipment.id,
        patch: {
          externalShipmentId: null,
        },
      },

      fetchPolicy: 'no-cache',
    });
  }
}

type CommitOptions = {
  policy: ScenarioCommitPolicy;
  statusUpdate?: (status: CommitStatus) => void;
  currentUser: CurrentUser;
  noSideEffects: boolean;
  committedData: CommittedPlanningData;
  suppressEmail: boolean;
  voyageNumberingResetsByShipId: Map<number, VoyageNumberingReset[]>;
  getScenarioData: (scenario: ScenarioSchedule) => {
    timelines: EventTimeline[];
  };
  /** Requested vessel scope for a scoped save; `undefined` = all vessels. Expanded internally via `computeScopeClosure`. */
  scopeVesselIds?: ReadonlySet<number>;
};

export async function buildCommit(
  client: ApolloClient<unknown>,
  originalScenario: UserSchedule,
  timelines: EventTimeline[],
  voyageNamingRuleByShipId: Map<
    number,
    {
      id: ShipVoyageNamingRuleRelationId;
      rule_id: VoyageNamingRuleId;
      rule: VoyageNamingRule_AdminFragment;
    }
  >,
  opts: CommitOptions,
): Promise<BuildCommitResult> {
  const {
    committedData,
    statusUpdate,
    policy,
    noSideEffects,
    currentUser,
    suppressEmail,
    voyageNumberingResetsByShipId,
    getScenarioData,
    scopeVesselIds,
  } = opts;

  let updatedScenario: InternalUserSchedule = cloneDeep(originalScenario);

  statusUpdate?.({ currentActionName: 'Checking archived items' });

  const archivedData = await client.query<
    ArchivedDataQueryResult['data'],
    ArchivedDataQueryVariables
  >({
    query: ArchivedDataDocument,
    fetchPolicy: 'no-cache',
  });

  if (!archivedData.data) {
    const msg = `Could not fetch data for archived items, cannot save.`;
    toast.error(msg);
    console.error(msg);
    throw new Error(msg);
  }

  updatedScenario = updateScenarioFromArchivedState(
    updatedScenario,
    parseArchivedData(archivedData.data),
    { unlinkArchivedPortCalls: false },
  );

  const originalClientIds = new Set(
    getAllScenarioEntries(originalScenario).map((e) => e.clientId),
  );
  const updatedClientIds = new Set(
    getAllScenarioEntries(updatedScenario).map((e) => e.clientId),
  );

  /** We can use this flag outside of this method to stop commit and have the user verify the updated scenario before trying again. */
  const hadArchivedItems =
    setDifference(originalClientIds, [updatedClientIds]).size > 0;

  if (hadArchivedItems) {
    // Recompute timelines with archived items removed
    statusUpdate?.({ currentActionName: 'Recomputing timelines' });
    const { timelines: updatedTimelines } = getScenarioData(updatedScenario);

    // Update scenario again with new timeline data
    updatedScenario = updateScenarioFromTimelines(
      updatedScenario,
      updatedTimelines,
      { trackChanges: false },
    );

    // Use the updated timelines for the rest of the commit process
    timelines = updatedTimelines;
  }

  statusUpdate?.({
    currentActionName: 'Checking main scenario state',
  });

  const { committedOrderById, shipmentByClientId } = committedData;
  updatedScenario = cleanupMissingOrdersFromScenario(
    updatedScenario,
    committedOrderById,
  );

  const { scenarioOrderById, scenarioShipmentByClientId } =
    getScenarioOrdersAndShipments(
      committedOrderById,
      shipmentByClientId,
      updatedScenario,
      {
        ignoreIntegrationCargoMargins:
          !!currentUser.organization?.configIgnoreIntegrationCargoMargins,
      },
    );

  const scenarioExistingVoyageCharterIds = uniq(
    [...updatedScenario.voyageCharterByOrderId.values()].filter(isNumber),
  ) as VoyageCharterId[];
  const scenarioAssignmentCoaIds = uniq(
    updatedScenario.coaAssignments.map((e) => e.coaId),
  );

  statusUpdate?.({ currentActionName: 'Checking voyage charters' });
  const vcRawData = await fetchVoyageCharterDataForCommit(
    client,
    scenarioAssignmentCoaIds,
    scenarioExistingVoyageCharterIds,
  );
  const voyageCharterData: ParsedVoyageCharterDataForCommit =
    parseVoyageCharterDataForCommit(vcRawData);

  statusUpdate?.({ currentActionName: 'Checking shipments' });

  const vesselsById: Map<number, Vessel> = new Map();

  for (const tl of timelines) {
    if (!tl.vessel || !tl.vesselId) continue;
    vesselsById.set(tl.vesselId, tl.vessel);
  }

  const ordersByShipmentId = mapGroupBy(
    [...scenarioOrderById.values()],
    (o) => o.shipmentId,
  );

  /** Only shipments which are used by some order in the selected scenario */
  const usedScenarioShipments = [...scenarioShipmentByClientId.values()].filter(
    (shipment) => ordersByShipmentId.has(shipment.clientId),
  );

  updatedScenario = updateScenarioFromTimelines(updatedScenario, timelines, {
    trackChanges: false,
  });

  const committedVesselByOrderId = new Map<number, number | null>();
  for (const entry of [
    ...committedData.tcOrderEntries.values(),
    ...committedData.completedTcOrderEntries.values(),
    ...committedData.coaEntries.values(),
    ...committedData.spotEntries.values(),
  ]) {
    committedVesselByOrderId.set(entry.orderId, entry.vesselId);
  }

  const allScenarioOrderAssignments = getOrderAssignments(updatedScenario);

  const currentVesselByOrderId = new Map<number, number | null>(
    [...allScenarioOrderAssignments].map(([orderId, entry]) => [
      orderId,
      entry.vesselId,
    ]),
  );

  const shipmentsWithVesselChange = getShipmentsWithVesselChange(
    usedScenarioShipments,
    ordersByShipmentId,
    currentVesselByOrderId,
    committedVesselByOrderId,
  );

  // Expand a requested vessel scope to include every vessel entangled by a
  // cargo reassignment; `createCommitInput` filters on this expanded set, and
  // the UI must name `affectedVesselIds` before the save proceeds.
  const scopeClosure = scopeVesselIds
    ? computeScopeClosure(
        scopeVesselIds,
        committedVesselByOrderId,
        currentVesselByOrderId,
      )
    : null;
  const expandedScope = scopeClosure?.expandedScope;
  const affectedVesselIds = scopeClosure?.affected ?? new Set<number>();

  // Built once and shared by both createCommitInput calls below so the scope
  // can never be dropped from one of them.
  const commitInputOpts: CreateCommitOptions = {
    currentUser,
    policy,
    suppressEmail,
    scopeVesselIds: expandedScope,
  };

  const referredShipmentsWithUpdatedNames = await applyShipmentNames(
    client,
    allScenarioOrderAssignments,
    vesselsById,
    voyageNamingRuleByShipId,
    noSideEffects,
    ordersByShipmentId,
    usedScenarioShipments,
    voyageNumberingResetsByShipId,
    shipmentsWithVesselChange,
  );

  const deletedCargoEntryOrderIds = new Set<number>();

  const { commit, deltas, committedEntryClientIds } = createCommitInput(
    committedData,
    updatedScenario,
    voyageCharterData,
    referredShipmentsWithUpdatedNames,
    scenarioOrderById,
    new Map(),
    commitInputOpts,
    deletedCargoEntryOrderIds,
    usedScenarioShipments,
  );

  if (!noSideEffects) {
    await cleanupConflictingShipmentsExternalIdsInTheDatabase(
      client,
      commit.createShipments
        ?.map((sh) => sh.externalShipmentId)
        .filter(isDefined),
      commit.updateShipments
        ?.map((sh) =>
          sh.shipmentPatch.externalShipmentId
            ? {
                shipmentId: sh.id,
                externalShipmentId: sh.shipmentPatch.externalShipmentId,
              }
            : undefined,
        )
        .filter(isDefined),
    );
  }

  if (deletedCargoEntryOrderIds.size === 0) {
    return {
      commit,
      deltas,
      updatedScenario,
      hadArchivedItems,
      affectedVesselIds,
      committedEntryClientIds,
    };
  }

  statusUpdate?.({
    currentActionName: 'Fetching additional data for deleted assignments',
  });

  const deletedAssignmentCleanupData = await fetchAssignmentCleanupData(
    client,
    deletedCargoEntryOrderIds,
  );

  return {
    ...createCommitInput(
      committedData,
      updatedScenario,
      voyageCharterData,
      referredShipmentsWithUpdatedNames,
      scenarioOrderById,
      deletedAssignmentCleanupData,
      commitInputOpts,
      deletedCargoEntryOrderIds,
      usedScenarioShipments,
    ),
    updatedScenario,
    hadArchivedItems,
    affectedVesselIds,
  };
}

type DoCommitOptions = {
  policy: ScenarioCommitPolicy;
  suppressEmail: boolean;
  statusUpdate?: (status: CommitStatus) => void;
  currentUser: CurrentUser;
  committedData: CommittedPlanningData;
  voyageNumberingResetsByShipId: Map<number, VoyageNumberingReset[]>;
  getScenarioData: (scenario: ScenarioSchedule) => {
    timelines: EventTimeline[];
  };
  /** Requested vessel scope for a scoped save; `undefined` = all vessels. */
  scopeVesselIds?: ReadonlySet<number>;
};

export function getUsedPortCallsInScenario(
  scenario: DeepReadOnly<
    Pick<
      InternalScenarioSchedule,
      'schedule' | 'coaAssignments' | 'spotAssignments'
    >
  >,
): Set<ClientId> {
  return new Set(
    compact([
      ...scenario.schedule.flatMap((e) => {
        if (isCargoEntry(e)) {
          return [e.loadingPortCallId, e.dischargingPortCallId];
        } else if (isSingleEntry(e)) {
          return [e.portCallId];
        } else return [];
      }),
      ...scenario.coaAssignments.flatMap((e) => [
        e.loadingPortCallId,
        e.dischargingPortCallId,
      ]),
      ...scenario.spotAssignments.flatMap((e) => [
        e.loadingPortCallId,
        e.dischargingPortCallId,
      ]),
    ]),
  );
}

/** Remove port calls not referenced by any schedule, COA, or spot entry */
export function pruneUnusedPortCalls<T extends InternalScenarioSchedule>(
  scenario: T,
): T {
  const used = getUsedPortCallsInScenario(scenario);
  if (used.size >= scenario.portCalls.length) return scenario;
  return {
    ...scenario,
    portCalls: scenario.portCalls.filter((pc) => used.has(pc.clientId)),
  };
}

export async function doCommitScenario(
  client: ApolloClient<unknown>,
  originalScenario: InternalUserSchedule,
  previousMaster: InternalMasterSchedule,
  timelines: EventTimeline[],
  voyageNamingRuleByShipId: Map<
    number,
    {
      id: ShipVoyageNamingRuleRelationId;
      rule_id: VoyageNamingRuleId;
      rule: VoyageNamingRule_AdminFragment;
    }
  >,
  opts: DoCommitOptions,
): Promise<null | {
  commitInput: CommitInput;
  commitResult: FetchResult<CommitMutation>;
  committedEntryClientIds: Set<ClientId> | null;
}> {
  const { statusUpdate, policy, getScenarioData } = opts;
  const { commit, updatedScenario, hadArchivedItems, committedEntryClientIds } =
    await buildCommit(
      client,
      originalScenario,
      timelines,
      voyageNamingRuleByShipId,
      {
        ...opts,
        noSideEffects: false,
        getScenarioData,
      },
    );

  if (isEmptyCommit(commit)) {
    toast.warn(`There were no changes to save`);
    return null;
  }

  if (hadArchivedItems) {
    toast.info(
      `Some items had been archived and were automatically removed from the scenario. Saving to main will proceed with the updated timeline.`,
    );
  }

  statusUpdate?.({ currentActionName: 'Saving data' });
  await delay(0); // Give status update a chance to update before executing query
  console.timeEnd('pre-commit');
  console.time('commit');

  const masterScenarioCommitLogClientId = generateClientId();
  const scenarioScenarioCommitLogClientId = generateClientId();
  commit.masterScenarioCommitLogClientId = masterScenarioCommitLogClientId;
  commit.scenarioScenarioCommitLogClientId = scenarioScenarioCommitLogClientId;

  const dropUnusedPortCallsFromScenario = <T extends InternalScenarioSchedule>(
    scenario: T,
  ): T => {
    const usedPortCallIds = getUsedPortCallsInScenario(scenario);

    return {
      ...scenario,
      portCalls: scenario.portCalls.filter((pc) =>
        usedPortCallIds.has(pc.clientId),
      ),
    };
  };

  void client.mutate<
    CreateCommitLogScenarioMutationResult['data'],
    CreateCommitLogScenarioMutationVariables
  >({
    mutation: CreateCommitLogScenarioDocument,
    variables: {
      input: {
        commitLogScenario: {
          scenarioData: serializeScenario(
            dropUnusedPortCallsFromScenario(previousMaster),
          ),
          clientId: masterScenarioCommitLogClientId,
          dataSchemaVersion: SCENARIO_DATA_SCHEMA_VERSION,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });
  void client.mutate<
    CreateCommitLogScenarioMutationResult['data'],
    CreateCommitLogScenarioMutationVariables
  >({
    mutation: CreateCommitLogScenarioDocument,
    variables: {
      input: {
        commitLogScenario: {
          scenarioData: serializeScenario(
            dropUnusedPortCallsFromScenario(updatedScenario),
          ),
          clientId: scenarioScenarioCommitLogClientId,
          dataSchemaVersion: SCENARIO_DATA_SCHEMA_VERSION,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  commit.previousCommitAt =
    policy === ScenarioCommitPolicy.OVERRIDE
      ? // For overrides, enough to know that master has been updated
        numToDateStringOpt(previousMaster?.masterCommitAt)
      : // For merges, we ensure that the scenario has been synced with master
        numToDateStringOpt(updatedScenario.masterCommitAt);

  const commitResult = await client.mutate<
    CommitMutationResult['data'],
    CommitMutationVariables
  >({
    mutation: CommitDocument,
    variables: {
      data: commit,
    },
    fetchPolicy: 'no-cache',
  });

  const errors = compact(commitResult.errors);
  if (errors.length > 0) {
    toast.error(
      `There were errors during saving. This has been notified. More details in browser console.`,
    );
    console.error(...errors);
    return null;
  }

  // The Commit mutation response only contains createdShipments.id, so the
  // Apollo cache never sees updated shipment fields.
  // Patch each renamed shipment in place so views
  // that read shipment.name from the cache reflect the new value.
  for (const update of commit.updateShipments ?? []) {
    const newName = update.shipmentPatch.name;
    if (newName == null) continue;
    client.cache.modify({
      id: client.cache.identify({ __typename: 'Shipment', id: update.id }),
      fields: { name: () => newName },
    });
  }

  return {
    commitInput: commit,
    commitResult: commitResult,
    committedEntryClientIds,
  };
}

const commitItemLabel: {
  [k in keyof Omit<
    CommitInput,
    | 'suppressEmail'
    | 'scenarioCID'
    | 'previousCommitAt'
    | 'isMerge'
    | 'masterScenarioCommitLogClientId'
    | 'scenarioScenarioCommitLogClientId'
  >]-?: string;
} = {
  createHandledByCoas: 'New COA assignments',
  createHandledBySpots: 'New Spot assignments',
  createOffHires: 'New off-hires',
  createPortCalls: 'New port calls',
  createScheduledOrders: 'New scheduled orders',
  createShipments: 'New voyages',
  createShipmentFreights: 'New shipment freights',
  createShipmentExtraFreights: 'New shipment extra freights',
  createSingleEvents: 'New single events',
  createCargoCompartmentAllocations: 'New cargo compartment allocations',

  updateHandledByCoas: 'Update COA assignments',
  updateHandledBySpots: 'Update spot assignments',
  updateOffHires: 'Update off-hires',
  updatePortCalls: 'Update port calls',
  updateScheduledOrders: 'Update scheduled orders',
  updateShipments: 'Update shipments',
  updateShipmentFreights: 'Update shipment freights',
  updateShipmentExtraFreights: 'Update shipment extra freights',
  updateSingleEvents: 'Update single events',
  updateVoyageCharters: 'Update voyage records',
  updateCargoCompartmentAllocations: 'Update cargo compartment allocations',

  deleteHandledByCoas: 'Delete COA assignments',
  deleteHandledBySpots: 'Delete spot assignments',
  deleteOffHires: 'Delete off-hires',
  deleteScheduledOrders: 'Delete scheduled orders',
  deleteSingleEvents: 'Delete single events',
  deleteVoyageCharters: 'Delete voyages',
  deleteCargoCompartmentAllocations: 'Delete cargo compartment allocations',

  updateOrders: 'Update orders',

  deleteOrderVoyageCharterRelations: 'Delete order voyage charter relations',
  deleteVoyageCharterOrderFreights: 'Delete voyage charter order freights',
  deleteVoyageCharterToContracts: 'Delete voyage charter to contract relations',
  deleteVoyageCharterPortData: 'Delete voyage charter port data',
  deletePortNotices: 'Delete port notices',
  deleteSingleEventPortCallRelations: 'Delete single event port call relations',
  deleteOrderPortCallRelations: 'Delete order port call relations',
  deleteBillOfLadingData: 'Delete bill of lading data',
  deleteCargoMeasurements: 'Delete cargo measurements',

  createOrderPortCallRelations: 'Create order port call relations',
  createSingleEventPortCallRelations: 'Create single event port call relations',
  createPortNotices: 'Create port notices',
  createVoyageCharters: 'Create voyage charters',
  createVoyageCharterPortData: 'Create voyage charter port data',
  createVoyageCharterToContracts: 'Create voyage charter to contract relations',
  createOrderVoyageCharterRelations: 'Create order voyage charter relations',
  createVoyageCharterOrderFreights: 'Create voyage charter order freights',

  updateVoyageCharterPortData: 'Update voyage charter port data',
  updateVoyageCharterToContracts: 'Update voyage charter to contract relations',
  updateVoyageCharterOrderFreights: 'Update voyage charter order freights',
  deletePortCalls: 'Delete port calls',
  deleteShipmentFreights: 'Delete shipment freights',
  deleteShipmentExtraFreights: 'Delete shipment extra freights',
};

/** Summarizes the changes in a commit input as numbers. The string keys are plain text descriptions of the changed item, numbers are the count of such changes.
 * // TODO: add a parameter to filter out changes for small (few millisecond) changes: @param timeThresholdMs Do not report changes for items with a change in time less than this threshold
 */
export function generateCommitSummary(commit: CommitInput): [string, number][] {
  const result: [string, number][] = [];

  for (const [k, label] of Object.entries(commitItemLabel) as [
    keyof typeof commitItemLabel,
    string,
  ][]) {
    const value = commit[k];
    if (isArray(value)) {
      const numItems = value.length ?? 0;
      if (numItems > 0) {
        result.push([label, numItems]);
      }
    }
  }

  return result;
}

export type TimelineDiffData = {
  assignmentsByOrderId: Map<
    `${number}-${'load' | 'discharge'}`,
    TimeData<CargoEvent>
  >;
  orderIdsByShipmentId: Map<ClientId, Set<number>>;
  eventsByClientId: Map<
    `${ClientId}-${'load' | 'discharge' | PortCallEventType}`,
    TimeData
  >;
};

export type TimelineDiffState = {
  hasChange: boolean;
  masterUpdated: boolean;
  masterDeleted: boolean;
  /** created, updated or deleted in scenario */
  scenarioControlled: boolean;
  scenarioDeleted: boolean;
  /** does assignment exist in master (might not have same values though) */
  isCommitted: boolean;
  hasOverlap: boolean;
  hasConflict: boolean;
};

export type ScenarioTimelineDiff = {
  master: TimelineDiffData;
  scenario: TimelineDiffData;
  /** Updates to entries looked up by the order id */
  ordersState: Map<number, TimelineDiffState>;
  /** Updates to entries looked up by the entry id */
  entriesState: Map<ClientId, TimelineDiffState>;
  /** Based on which shipment each order belongs to */
  orderToShipmentState: Map<number, TimelineDiffState>;
  /** Based on which orders each shipment has */
  shipmentToOrdersState: Map<ClientId, TimelineDiffState>;
};

export const isSameAssignment = (
  a: TimeData | undefined,
  b: TimeData | undefined,
): boolean => {
  if (a === b) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  const ignoreTdKeys: (keyof TimeData)[] = [
    'isFirstShipmentEvent',
    'isFirstEvent',
    'isLastEvent',
    'canPortCallSwapLeft',
    'canPortCallSwapRight',
    'index',
    'physicalTimeConflict',
    'previousEventStart',
    'startOverrideConflict',
  ];

  /** We don't need to check these keys on the Event as long as they match on the computed TD */
  const ignoreEventKeys: (keyof Events)[] = ['start', 'end', 'duration'];

  return (
    isShallowEqual(a, b, ignoreTdKeys) &&
    isShallowEqual(a.event, b.event, ignoreEventKeys) &&
    a.event.entry.clientId === b.event.entry.clientId
  );
};

/** Returns a set of ClientId's for order assignments in the UserScenario which have an overlap with another assignment in committed (master) scenario */
function getOverlappingConflicts(
  masterTimelines: EventTimeline[],
  scenarioTimelines: EventTimeline[],
  scenario: Pick<UserSchedule | EstimateTimeline, 'entryStates'>,
): Set<ClientId> {
  const result = new Set<ClientId>();

  const masterTimelinesById = mapKeyBy(masterTimelines, (t) => t.id);
  const scenarioTimelinesById = mapKeyBy(scenarioTimelines, (t) => t.id);
  const allTimelineIds = uniq([
    ...masterTimelinesById.keys(),
    ...scenarioTimelinesById.keys(),
  ]);

  for (const timelineId of allTimelineIds) {
    const masterTimeline = masterTimelinesById.get(timelineId);
    const scenarioTimeline = scenarioTimelinesById.get(timelineId);
    if (!(masterTimeline && scenarioTimeline)) {
      continue;
    }

    const scenarioTimelineOrderIds = new Set<number>();
    const scenarioEvents: TimeData<CargoEvent>[] = [];
    scenarioTimeline.voyageGroups.forEach((vg) =>
      vg.events.forEach((td) => {
        if (isCargoEventTimeData(td)) {
          scenarioEvents.push(td);
          scenarioTimelineOrderIds.add(td.event.entry.orderId);
        }
      }),
    );

    const masterEvents: TimeData<CargoEvent>[] = [];
    masterTimeline.voyageGroups.forEach((vg) =>
      vg.events.forEach((td) => {
        if (isCargoEventTimeData(td)) {
          const { clientId, orderId } = td.event.entry;
          if (
            // Deleted entries not relevant to overlap, they are not present in result anyway
            !isEntryStateDeleted(scenario, clientId) &&
            // Assignments that are on both timelines not relevant as their timing is already considered in scenario
            !scenarioTimelineOrderIds.has(orderId)
          ) {
            masterEvents.push(td);
          }
        }
      }),
    );

    type TaggedEvent = readonly [
      ts: number,
      isStart: boolean,
      isMaster: boolean,
      td: TimeData<CargoEvent>,
    ];

    /** Combined sequence of events, tagged by whether it's the start or end of an entry */
    const taggedEvents: TaggedEvent[] = sortBy(
      [
        ...masterEvents.map((e) =>
          isLoadEvent(e.event)
            ? ([e.start, true, true, e] as const)
            : ([e.end, false, true, e] as const),
        ),
        ...scenarioEvents.map((e) =>
          isLoadEvent(e.event)
            ? ([e.start, true, false, e] as const)
            : ([e.end, false, false, e] as const),
        ),
      ],
      (e) => e[0],
    );

    const activeMasterEntries = new Set<ClientId>();
    const activeScenarioEntries = new Set<ClientId>();
    for (const [, isStart, isMaster, td] of taggedEvents) {
      const clientId = td.event.entry.clientId;
      const set = isMaster ? activeMasterEntries : activeScenarioEntries;
      if (isStart) {
        set.add(clientId);
      } else {
        set.delete(clientId);
      }
      const masterNotInScenario = setDifference(activeMasterEntries, [
        activeScenarioEntries,
      ]);
      const scenarioNotInMaster = setDifference(activeScenarioEntries, [
        activeMasterEntries,
      ]);

      /** Both timelines have an entry at current time which is not present in the other */
      if (masterNotInScenario.size > 0 && scenarioNotInMaster.size > 0) {
        for (const scenarioCID of scenarioNotInMaster) {
          result.add(scenarioCID);
        }
      }
    }
  }

  return result;
}

export function hasTimelineChanged(
  timeline: EventTimeline,
  timelineDiff: ScenarioTimelineDiff,
): boolean {
  return timeline.voyageGroups.some((vg) =>
    vg.events.some(
      (td) =>
        timelineDiff.entriesState.get(td.event.entry.clientId)?.hasChange ||
        (isCargoEventTimeData(td) &&
          timelineDiff.orderToShipmentState.get(td.event.order.id)?.hasChange),
    ),
  );
}

export const mergeTimeDataDiffs = (
  ...diffs: (TimelineDiffState | null)[]
): TimelineDiffState => {
  return diffs.reduce<TimelineDiffState>(
    (acc, curr) =>
      !curr
        ? acc
        : {
            hasChange: acc.hasChange || curr.hasChange,
            masterUpdated: acc.masterUpdated || curr.masterUpdated,
            masterDeleted: acc.masterDeleted || curr.masterDeleted,
            scenarioControlled:
              acc.scenarioControlled || curr.scenarioControlled,
            scenarioDeleted: acc.scenarioDeleted || curr.scenarioDeleted,
            isCommitted: acc.isCommitted || curr.isCommitted,
            hasOverlap: acc.hasOverlap || curr.hasOverlap,
            hasConflict: acc.hasConflict || curr.hasConflict,
          },
    {
      hasChange: false,
      masterUpdated: false,
      masterDeleted: false,
      scenarioControlled: false,
      scenarioDeleted: false,
      isCommitted: false,
      hasOverlap: false,
      hasConflict: false,
    },
  );
};

/** Returns a comparison between timelines of committed (master) vs scenario */
export const getScenarioTimelinesDiff = memoize3(
  function getScenarioTimelinesDiff(
    masterTimelines: EventTimeline[],
    scenarioTimelines: EventTimeline[],
    scenario: Pick<
      UserSchedule | SuggestionSchedule | EstimateTimeline,
      'type' | 'entryStates'
    >,
  ): ScenarioTimelineDiff {
    const allOrderIds = new Set<number>();
    const allEntries = new Map<
      ClientId,
      CargoEntry['category'] | PortCallEntry['category']
    >();

    function buildAssignmentMaps(timelines: EventTimeline[]): TimelineDiffData {
      const eventsByClientId: TimelineDiffData['eventsByClientId'] = new Map();
      const assignmentsByOrderId: TimelineDiffData['assignmentsByOrderId'] =
        new Map();
      const orderIdsByShipmentId = new DefaultMap<ClientId, Set<number>>(
        () => new Set<number>(),
      );
      timelines.forEach((t) => {
        t.voyageGroups.forEach((vg) => {
          vg.events.forEach((td) => {
            if (isCargoEventTimeData(td)) {
              const { event } = td;
              const cId = event.entry.clientId;
              const orderId = event.order.id;
              const eventTypeName =
                td.event.type === CoreEventType.Discharge
                  ? 'discharge'
                  : 'load';
              assignmentsByOrderId.set(`${orderId}-${eventTypeName}`, td);
              eventsByClientId.set(`${cId}-${eventTypeName}`, td);
              allOrderIds.add(orderId);
              if (event.order.shipment) {
                orderIdsByShipmentId
                  .get(event.order.shipment.clientId)
                  .add(orderId);
              }
              allEntries.set(cId, event.entry.category);
            } else if (isPortCallEventTimeData(td)) {
              const { event } = td;
              const cId = event.entry.clientId;
              eventsByClientId.set(`${cId}-${event.event}`, td);
              allEntries.set(cId, event.entry.category);
            }
          });
        });
      });
      return { assignmentsByOrderId, orderIdsByShipmentId, eventsByClientId };
    }

    const masterMaps: TimelineDiffData = buildAssignmentMaps(masterTimelines);
    const scenarioMaps: TimelineDiffData =
      buildAssignmentMaps(scenarioTimelines);

    const overlaps =
      isUserScenario(scenario) || isEstimateScenario(scenario)
        ? getOverlappingConflicts(masterTimelines, scenarioTimelines, scenario)
        : null;

    const getTimeDataDiff = (
      masterAssignment: TimeData | undefined,
      scenarioAssignment: TimeData | undefined,
    ): TimelineDiffState => {
      const masterCId = masterAssignment?.event.entry.clientId;
      const scenarioCId = scenarioAssignment?.event.entry.clientId;
      const scenarioControlled = !scenarioCId
        ? false
        : isEntryStateUpdated(scenario, scenarioCId) ||
          (masterCId ? isEntryStateDeleted(scenario, masterCId) : false);
      const scenarioDeleted = !masterCId
        ? false
        : isEntryStateDeleted(scenario, masterCId);
      const hasChange =
        !isSameAssignment(masterAssignment, scenarioAssignment) ||
        scenarioDeleted;
      const masterDeleted = !masterAssignment && !scenarioControlled;
      const masterUpdated =
        hasChange &&
        (masterAssignment?.event.entry.updatedAt ?? 0) >
          (scenarioAssignment?.event.entry.updatedAt ?? 0) &&
        !scenarioDeleted;
      const hasOverlap = (scenarioCId && overlaps?.has(scenarioCId)) ?? false;
      const hasConflict =
        hasChange && scenarioControlled && (masterUpdated || masterDeleted);

      return {
        hasChange,
        masterDeleted,
        masterUpdated,
        scenarioControlled,
        scenarioDeleted,
        isCommitted: !!masterAssignment,
        hasOverlap,
        hasConflict,
      };
    };
    const ordersState: ScenarioTimelineDiff['ordersState'] = new Map(
      [...allOrderIds].map((orderId) => {
        const eventTypes = ['load', 'discharge'] as const;
        const diffs = eventTypes.map((eventType) => {
          const masterEvent = masterMaps.assignmentsByOrderId.get(
            `${orderId}-${eventType}`,
          );
          const scenarioEvent = scenarioMaps.assignmentsByOrderId.get(
            `${orderId}-${eventType}`,
          );
          return getTimeDataDiff(masterEvent, scenarioEvent);
        });
        return [orderId, mergeTimeDataDiffs(...diffs)];
      }),
    );

    const entriesState: ScenarioTimelineDiff['entriesState'] = new Map(
      [...allEntries].map(([entryCId, entryCategory]) => {
        const eventTypes =
          entryCategory === EventCategory.PortCall
            ? ([
                PortCallEventType.Arrival,
                PortCallEventType.Berthing,
                PortCallEventType.Departure,
              ] as const)
            : (['load', 'discharge'] as const);
        const diffs = eventTypes.map((eventType) => {
          const masterEvent = masterMaps.eventsByClientId.get(
            `${entryCId}-${eventType}`,
          );
          const scenarioEvent = scenarioMaps.eventsByClientId.get(
            `${entryCId}-${eventType}`,
          );
          return getTimeDataDiff(masterEvent, scenarioEvent);
        });
        const diff = mergeTimeDataDiffs(...diffs);

        return [entryCId, diff];
      }),
    );

    const orderToShipmentState: ScenarioTimelineDiff['orderToShipmentState'] =
      new Map(
        [...allOrderIds].map((orderId) => {
          const masterAssignment = masterMaps.assignmentsByOrderId.get(
            `${orderId}-load`,
          );
          const scenarioAssignment = scenarioMaps.assignmentsByOrderId.get(
            `${orderId}-load`,
          );
          const masterShipmentCID =
            masterAssignment?.event.order.shipment?.clientId;
          const scenarioShipmentCID =
            scenarioAssignment?.event.order.shipment?.clientId;

          const diff: TimelineDiffState = {
            hasChange: masterShipmentCID !== scenarioShipmentCID,
            hasConflict: false,
            hasOverlap: false,
            isCommitted: masterShipmentCID !== undefined,
            masterDeleted: masterAssignment === undefined,
            masterUpdated: false,
            scenarioControlled:
              !!scenarioShipmentCID &&
              isEntryStateUpdated(scenario, scenarioShipmentCID),
            scenarioDeleted:
              !!scenarioShipmentCID &&
              isEntryStateDeleted(scenario, scenarioShipmentCID),
          };
          return [orderId, diff];
        }),
      );

    const allShipmentIds = new Set<ClientId>([
      ...masterMaps.orderIdsByShipmentId.keys(),
      ...scenarioMaps.orderIdsByShipmentId.keys(),
    ]);

    const shipmentToOrdersState: ScenarioTimelineDiff['shipmentToOrdersState'] =
      new Map(
        [...allShipmentIds].map((shipmentId) => {
          const masterOrderIds =
            masterMaps.orderIdsByShipmentId.get(shipmentId);
          const scenarioOrderIds =
            scenarioMaps.orderIdsByShipmentId.get(shipmentId);

          const scenarioControlled = [...(masterOrderIds ?? [])].some((oid) => {
            const diff = ordersState.get(oid);
            return diff?.scenarioControlled || diff?.scenarioDeleted || false;
          });
          const scenarioDeleted =
            !!masterOrderIds?.size &&
            [...masterOrderIds].every((oid) => {
              const diff = ordersState.get(oid);
              return !!diff && diff.scenarioDeleted;
            });

          const diff: TimelineDiffState = {
            hasChange:
              masterOrderIds?.size !== scenarioOrderIds?.size ||
              (!!masterOrderIds &&
                setSome(masterOrderIds, (oid) => !scenarioOrderIds?.has(oid))),
            hasConflict: false,
            hasOverlap: false,
            isCommitted: !!masterOrderIds,
            masterDeleted: !masterOrderIds && !!scenarioOrderIds,
            masterUpdated: false,
            scenarioControlled,
            scenarioDeleted,
          };
          return [shipmentId, diff];
        }),
      );
    return {
      master: masterMaps,
      scenario: scenarioMaps,
      ordersState,
      entriesState,
      orderToShipmentState,
      shipmentToOrdersState,
    };
  },
);

/**
 * Build an updated committed (master) scenario for display as the result of a tentative commit.
 * We use the `commitInput` as the instruction for which records are affected but pick the
 * actual values from the scenario to commit, `commitScenario`.
 */
export const previewMasterScenarioFromCommitInput = memoize4(
  function previewMasterScenarioFromCommitInput<
    T extends UserSchedule | SuggestionSchedule,
  >(
    master: MasterSchedule,
    userScenario: T,
    committedData: CommittedPlanningData,
    commitInput: CommitInput,
  ): T {
    const checkIsDeleted = (
      entry: Entry,
      committedMap: Map<ClientId, WithDbId<Entry, number>>,
      deletionsArray: number[] | null | undefined,
    ): boolean => {
      const committed = committedMap.get(entry.clientId);
      return !!committed && !!deletionsArray?.includes(committed.dbId);
    };

    const scenarioScheduleByClientId = mapKeyBy(
      userScenario.schedule,
      (e) => e.clientId,
    );
    const scenarioCoasByClientId = mapKeyBy(
      userScenario.coaAssignments,
      (e) => e.clientId,
    );
    const scenarioSpotsByClientId = mapKeyBy(
      userScenario.spotAssignments,
      (e) => e.clientId,
    );
    const scenarioPortCallsByClientId = mapKeyBy(
      userScenario.portCalls,
      (e) => e.clientId,
    );

    const schedule: UserSchedule['schedule'] = master.schedule
      .filter((e) => {
        if (isTCOrderEntry(e)) {
          return !checkIsDeleted(
            e,
            committedData.tcOrderEntries,
            commitInput.deleteScheduledOrders,
          );
        } else if (isSingleEntry(e)) {
          return !checkIsDeleted(
            e,
            committedData.singleEventEntriesByClientId,
            commitInput.deleteSingleEvents,
          );
        } else if (isOffHireEntry(e)) {
          return !checkIsDeleted(
            e,
            committedData.offHireEntriesByClientId,
            commitInput.deleteOffHires,
          );
        } else {
          exhaustiveCheck(
            e,
            'unexpected entry type in schedule for previewMasterScenarioFromCommitInput',
          );
        }
      })
      .map((e) => {
        let hasUpdate = false;
        if (isTCOrderEntry(e)) {
          const committed = committedData.tcOrderEntries.get(e.clientId);
          hasUpdate = !!(
            committed &&
            commitInput.updateScheduledOrders?.find(
              (u) => u.id === committed.dbId,
            )
          );
        } else if (isSingleEntry(e)) {
          const committed = committedData.singleEventEntriesByClientId.get(
            e.clientId,
          );
          hasUpdate = !!(
            committed &&
            commitInput.updateSingleEvents?.find((u) => u.id === committed.dbId)
          );
        } else if (isOffHireEntry(e)) {
          const committed = committedData.offHireEntriesByClientId.get(
            e.clientId,
          );
          hasUpdate = !!(
            committed &&
            commitInput.updateOffHires?.find((u) => u.id === committed.dbId)
          );
        } else {
          exhaustiveCheck(
            e,
            'unexpected entry type in schedule for previewMasterScenarioFromCommitInput',
          );
        }
        return hasUpdate
          ? assertedDefined(
              scenarioScheduleByClientId.get(e.clientId),
              `expected committed entry to exist in scenario`,
            )
          : e;
      })
      .concat(
        [
          ...(commitInput.createScheduledOrders ?? []),
          ...(commitInput.createSingleEvents ?? []),
          ...(commitInput.createOffHires ?? []),
        ].map((v) =>
          assertedDefined(
            scenarioScheduleByClientId.get(
              parseClientId(
                assertedDefined(
                  v.clientId,
                  `expected created entry to have clientId`,
                ),
              ),
            ),
            `expected created entry to exist in scenario`,
          ),
        ),
      );

    const coaAssignments: UserSchedule['coaAssignments'] = master.coaAssignments
      .filter((e) => {
        return !checkIsDeleted(
          e,
          committedData.coaEntries,
          commitInput.deleteHandledByCoas,
        );
      })
      .map((e) => {
        const committed = committedData.coaEntries.get(e.clientId);
        const hasUpdate = !!(
          committed &&
          commitInput.updateHandledByCoas?.find((u) => u.id === committed.dbId)
        );
        return hasUpdate
          ? assertedDefined(
              scenarioCoasByClientId.get(e.clientId),
              `expected committed entry to exist in scenario`,
            )
          : e;
      })
      .concat(
        [...(commitInput.createHandledByCoas ?? [])].map((v) =>
          assertedDefined(
            scenarioCoasByClientId.get(
              parseClientId(
                assertedDefined(
                  v.clientId,
                  `expected created entry to have clientId`,
                ),
              ),
            ),
            `expected created entry to exist in scenario`,
          ),
        ),
      );

    const spotAssignments: UserSchedule['spotAssignments'] =
      master.spotAssignments
        .filter((e) => {
          return !checkIsDeleted(
            e,
            committedData.spotEntries,
            commitInput.deleteHandledBySpots,
          );
        })
        .map((e) => {
          const committed = committedData.spotEntries.get(e.clientId);
          const hasUpdate = !!(
            committed &&
            commitInput.updateHandledBySpots?.find(
              (u) => u.id === committed.dbId,
            )
          );
          return hasUpdate
            ? assertedDefined(
                scenarioSpotsByClientId.get(e.clientId),
                `expected committed entry to exist in scenario`,
              )
            : e;
        })
        .concat(
          [...(commitInput.createHandledBySpots ?? [])].map((v) =>
            assertedDefined(
              scenarioSpotsByClientId.get(
                parseClientId(
                  assertedDefined(
                    v.clientId,
                    `expected created entry to have clientId`,
                  ),
                ),
              ),
              `expected created entry to exist in scenario`,
            ),
          ),
        );

    const portCalls: UserSchedule['portCalls'] = master.portCalls
      .filter((e) => {
        return !checkIsDeleted(
          e,
          committedData.portCallEntriesByClientId,
          commitInput.deletePortCalls,
        );
      })
      .map((e) => {
        const committed = committedData.portCallEntriesByClientId.get(
          e.clientId,
        );
        const hasUpdate = !!(
          committed &&
          commitInput.updatePortCalls?.find((u) => u.id === committed.dbId)
        );
        return hasUpdate
          ? assertedDefined(
              scenarioPortCallsByClientId.get(e.clientId),
              `expected committed entry to exist in scenario`,
            )
          : e;
      })
      .concat(
        [...(commitInput.createPortCalls ?? [])].map((v) =>
          assertedDefined(
            scenarioPortCallsByClientId.get(
              parseClientId(
                assertedDefined(
                  v.clientId,
                  `expected created entry to have clientId`,
                ),
              ),
            ),
            `expected created entry to exist in scenario`,
          ),
        ),
      );

    const patchOrders: UserSchedule['patchOrders'] = new Map(
      (commitInput.updateOrders ?? []).map((x) => [
        x.id as OrderId,
        assertedDefined(
          userScenario.patchOrders.get(x.id as OrderId),
          `expected order to update have an order patch in scenario`,
        ),
      ]),
    );

    const committedPortCallsByDbId = committedData.portCallEntriesByDbId;
    const committedSingleEventsByDbId = mapKeyBy(
      committedData.allScheduleData.filter(
        (e): e is WithDbId<Required<SingleEntry>, SingleEventId> =>
          isSingleEntry(e),
      ),
      (e) => e.dbId,
    );

    const newOrderPortCallRelations = new DefaultMap<
      boolean,
      Map<number, ClientId>
    >(() => new Map());
    const newSingleEventPortCallRelations = new Map<ClientId, ClientId>();

    for (const pcrel of commitInput.createOrderPortCallRelations ?? []) {
      const { isLoadPort, orderId, portCallId } = pcrel;
      if (portCallId.existing != undefined) {
        const pc = assertedDefined(
          committedPortCallsByDbId.get(portCallId.existing),
          `expected existing port call to be found in committed state`,
        );
        newOrderPortCallRelations.get(isLoadPort).set(orderId, pc.clientId);
      } else if (portCallId.created != undefined) {
        const newPc = get(
          commitInput.createPortCalls ?? [],
          portCallId.created,
        );
        assertDefined(
          newPc?.clientId,
          `expected referred created port call to have been created in CommitInput with clientId`,
        );
        newOrderPortCallRelations
          .get(isLoadPort)
          .set(orderId, parseClientId(newPc.clientId));
      } else {
        throw new Error(`expected createdOrExisting to have either value`);
      }
    }

    for (const pcrel of commitInput.createSingleEventPortCallRelations ?? []) {
      const { singleEventId, portCallId } = pcrel;
      let singleEventCId: ClientId;

      if (singleEventId.existing != undefined) {
        const singleEvent = assertedDefined(
          committedSingleEventsByDbId.get(
            singleEventId.existing as SingleEventId,
          ),
          `expected existing single event to be found in committed state`,
        );
        singleEventCId = singleEvent.clientId;
      } else if (singleEventId.created != undefined) {
        const newSingleEvent = get(
          commitInput.createSingleEvents ?? [],
          singleEventId.created,
        );
        assertDefined(
          newSingleEvent?.clientId,
          `expected referred created single event to have been created in CommitInput with clientId`,
        );
        singleEventCId = parseClientId(newSingleEvent.clientId);
      } else {
        throw new Error(`expected createdOrExisting to have either value`);
      }

      if (portCallId.existing != undefined) {
        const pc = assertedDefined(
          committedPortCallsByDbId.get(portCallId.existing),
          `expected existing port call to be found in committed state`,
        );
        newSingleEventPortCallRelations.set(singleEventCId, pc.clientId);
      } else if (portCallId.created != undefined) {
        const newPc = get(
          commitInput.createPortCalls ?? [],
          portCallId.created,
        );
        assertDefined(
          newPc?.clientId,
          `expected referred created port call to have been created in CommitInput with clientId`,
        );
        newSingleEventPortCallRelations.set(
          singleEventCId,
          parseClientId(newPc.clientId),
        );
      } else {
        throw new Error(`expected createdOrExisting to have either value`);
      }
    }

    const updatePortCallId = <T extends Entry>(e: T): T => {
      if (isCargoEntry(e)) {
        const newLoadPortCallId = newOrderPortCallRelations
          .get(true)
          .get(e.orderId);
        const newDischargePortCallId = newOrderPortCallRelations
          .get(false)
          .get(e.orderId);
        if (newLoadPortCallId || newDischargePortCallId) {
          const result = cloneDeep(e);
          if (newLoadPortCallId) {
            result.loadingPortCallId = newLoadPortCallId;
          }
          if (newDischargePortCallId) {
            result.dischargingPortCallId = newDischargePortCallId;
          }
          return result;
        }
      } else if (isSingleEntry(e)) {
        const newPortCallId = newSingleEventPortCallRelations.get(e.clientId);
        if (newPortCallId) {
          const result = cloneDeep(e);
          result.portCallId = newPortCallId;
          return result;
        }
      }
      return e;
    };

    return {
      ...userScenario,
      clientId: parseClientId('commit-result-scenario'),
      entryStates: new Map(),
      syncedWithMasterAt: Date.now(),
      changedAt: Date.now(),
      createdAt: new Date().toISOString(),

      schedule: schedule.map(updatePortCallId),
      coaAssignments: coaAssignments.map(updatePortCallId),
      spotAssignments: spotAssignments.map(updatePortCallId),
      portCalls,
      patchOrders,
    };
  },
);

export function isAutoSyncEnabledForScenario(
  orgSettingIsEnabled: boolean,
  scenario: ScenarioSchedule,
): boolean {
  return scenario.autoUpdateFromMaster === 'always'
    ? true
    : scenario.autoUpdateFromMaster === 'never'
      ? false
      : orgSettingIsEnabled;
}
